/**
 * Elite Batch 17 Part 1 — Workflow Templates 1548–1567
 * Lifecycle-stage progression nurture, lost deal re-engagement, lost deal win-back,
 * MQL nurture by company size, MQL nurture by industry, MQL nurture by persona,
 * MQL nurture by product interest, Marketo lead lifecycle stage manager,
 * Marketo lead scoring automation, Marketo sales alert notification,
 * meeting follow-up automator, meeting no-show reschedule, meeting notes to CRM logger,
 * meeting-booked reminder sequence, Monday.com to HubSpot deal sync,
 * multi CRM sync bridge, multi-asset content nurture by topic,
 * multi-touch nurture with engagement program, new contact at existing account welcome,
 * new MQL to SDR task plus SLA timer.
 *
 * DESIGN RULES (inherited from prebuiltWorkflows.ts):
 * 1. Every variable MUST come from a real API response field or AI-generated output
 * 2. No magic variables — if a condition checks something, an upstream node must produce it
 * 3. Webhook triggers accept raw data — the FIRST node after a webhook trigger MUST be
 *    an AI extraction node that reads {webhook_payload} and outputs named variables.
 *    NEVER assume webhook payloads have specific field names (no {email}, {name}, etc.)
 * 4. Every condition has a REAL value (never blank, never "?")
 * 5. Services are pre-selected — user never sees "pick a service"
 * 6. Output variable names use the actual API response field paths
 * 7. Trigger inputFields should be EMPTY for webhooks — the AI extraction node declares outputs
 * 8. AI prompts for extraction should say "find the X regardless of format" to handle variations
 */

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}
function smsNode(
  id: string,
  label: string,
  y: number,
  to: string,
  message: string,
  x = 80,
): any {
  return n(id, "sendSMS", label, y, { sendSmsDetails: { to, message } }, x);
}
function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function hubspotGetNode(
  id: string,
  label: string,
  y: number,
  endpoint: string,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "GET",
        url: `https://api.hubapi.com/${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {hubspot_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [],
        variables: vars,
      },
    },
    x,
  );
}
function hubspotPostNode(
  id: string,
  label: string,
  y: number,
  endpoint: string,
  bodyFields: Array<{ key: string; value: string }>,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://api.hubapi.com/${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {hubspot_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: bodyFields,
        variables: vars,
      },
    },
    x,
  );
}

// =============================================================================
// ELITE BATCH 17 PART 1 — TEMPLATES 1548–1567
// =============================================================================

export const ELITE_BATCH17_PART1: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ===================================================================
  // 1548. LIFECYCLE-STAGE PROGRESSION NURTURE (9 nodes)
  // ===================================================================

  "Lifecycle-Stage Progression Nurture": {
    summary:
      "Receives a lifecycle stage change webhook, AI-extracts the stage transition data, loads prior journey context from HubSpot, maps stage-to-content with runCode, sends a stage-appropriate transition email, enrolls the contact in the right HubSpot nurture program, adjusts pacing with runCode, conditionally alerts sales if MQL/SQL, and logs conversion timing to Sheets.",
    nodes: [
      trigger("Webhook: Contact lifecycle stage changed", "webhook_app", {
        source: "hubspot",
        event: "contact.lifecycle_stage_change",
        howItStarts:
          "Fires when a contact's lifecycle stage changes in HubSpot (e.g., Subscriber to Lead, Lead to MQL, MQL to SQL), sending the raw stage-change event payload",
        setup:
          "Configure HubSpot webhook subscription for contact.propertyChange on lifecyclestage property. Ensure nurture program lists exist in HubSpot. Prepare Google Sheets for conversion timing log.",
        inputFields: [],
      }),

      // 2. AI extract stage data
      aiNode(
        "2",
        "AI extract lifecycle stage transition data",
        280,
        "Extract lifecycle stage transition data from this raw webhook payload. Find the contact email, contact name, contact ID, previous lifecycle stage, new lifecycle stage, timestamp of change, company name, lead source, and any associated deal IDs regardless of field naming format. Return JSON: {contact_email, contact_name, contact_id, previous_stage, new_stage, change_timestamp, company_name, lead_source, deal_ids}",
        "Raw webhook payload: {webhook_payload}",
        [
          { field: "contact_email", as: "contact_email", type: "string" },
          { field: "contact_name", as: "contact_name", type: "string" },
          { field: "contact_id", as: "contact_id", type: "string" },
          { field: "previous_stage", as: "previous_stage", type: "string" },
          { field: "new_stage", as: "new_stage", type: "string" },
          { field: "change_timestamp", as: "stage_change_ts", type: "string" },
          { field: "company_name", as: "company_name", type: "string" },
          { field: "lead_source", as: "lead_source", type: "string" },
          { field: "deal_ids", as: "deal_ids", type: "string" },
        ],
      ),

      // 3. Prior journey context loader from HubSpot
      hubspotGetNode(
        "3",
        "Load prior journey context from HubSpot",
        520,
        "crm/v3/objects/contacts/{contact_id}?properties=email,firstname,lastname,lifecyclestage,hs_lead_status,num_associated_deals,hs_email_last_open_date,hs_analytics_num_page_views,notes_last_updated,hs_sequences_actively_enrolled_count",
        [
          {
            name: "properties.hs_lead_status",
            customName: "lead_status",
            type: "string",
          },
          {
            name: "properties.num_associated_deals",
            customName: "associated_deals_count",
            type: "string",
          },
          {
            name: "properties.hs_email_last_open_date",
            customName: "last_email_open",
            type: "string",
          },
          {
            name: "properties.hs_analytics_num_page_views",
            customName: "total_page_views",
            type: "string",
          },
          {
            name: "properties.hs_sequences_actively_enrolled_count",
            customName: "active_sequences",
            type: "string",
          },
        ],
      ),

      // 4. Stage-to-content mapper runCode
      n(
        "4",
        "runCode",
        "Map lifecycle stage to content and nurture program",
        760,
        {
          runCodeDetails: {
            code: `const stageMap = {
  'subscriber_to_lead': { content: 'educational_intro', program: 'awareness_nurture', pace: 7, sales_alert: false },
  'lead_to_marketingqualifiedlead': { content: 'solution_comparison', program: 'mql_nurture', pace: 5, sales_alert: true },
  'marketingqualifiedlead_to_salesqualifiedlead': { content: 'roi_case_study', program: 'sql_accelerator', pace: 3, sales_alert: true },
  'salesqualifiedlead_to_opportunity': { content: 'implementation_guide', program: 'opportunity_support', pace: 2, sales_alert: true },
  'opportunity_to_customer': { content: 'onboarding_welcome', program: 'customer_onboarding', pace: 1, sales_alert: false },
};
const key = (variables.previous_stage + '_to_' + variables.new_stage).toLowerCase().replace(/ /g, '');
const mapping = stageMap[key] || { content: 'general_nurture', program: 'default_nurture', pace: 7, sales_alert: false };
output.content_type = mapping.content;
output.nurture_program = mapping.program;
output.email_pace_days = mapping.pace;
output.should_alert_sales = mapping.sales_alert ? 'yes' : 'no';
output.transition_label = variables.previous_stage + ' → ' + variables.new_stage;`,
            outputVariables: [
              { name: "content_type", type: "string" },
              { name: "nurture_program", type: "string" },
              { name: "email_pace_days", type: "number" },
              { name: "should_alert_sales", type: "string" },
              { name: "transition_label", type: "string" },
            ],
          },
        },
      ),

      // 5. Transition email
      emailNode(
        "5",
        "Send stage-appropriate transition email",
        1000,
        "{contact_email}",
        "Your journey with us just leveled up, {contact_name}",
        "Hi {contact_name},\\n\\nGreat news! You have progressed to a new stage in your journey with us.\\n\\nTransition: {transition_label}\\nCompany: {company_name}\\n\\nBased on where you are now, we have curated content specifically for you. Your personalized content track: {content_type}\\n\\nWe will be sending you the most relevant resources over the coming days to help you get the most value from this stage.\\n\\nIf you have any questions, do not hesitate to reach out.\\n\\nBest regards,\\nThe Team",
      ),

      // 6. Nurture program enrollor HubSpot
      hubspotPostNode(
        "6",
        "Enroll contact in stage-specific nurture program",
        1240,
        "crm/v3/objects/contacts/{contact_id}/associations/lists",
        [
          {
            key: "properties",
            value:
              '{"hs_lead_status":"{new_stage}","hs_sequences_actively_enrolled_count":"1","notes_last_updated":"{stage_change_ts}"}',
          },
        ],
        [
          { name: "id", customName: "enrollment_id", type: "string" },
          {
            name: "properties.hs_lead_status",
            customName: "updated_status",
            type: "string",
          },
        ],
      ),

      // 7. Pace adjuster runCode
      n(
        "7",
        "runCode",
        "Calculate next touch date and engagement score",
        1480,
        {
          runCodeDetails: {
            code: `const pacedays = parseInt(variables.email_pace_days) || 5;
const changeDate = new Date(variables.stage_change_ts || Date.now());
const nextTouch = new Date(changeDate.getTime() + pacedays * 24 * 60 * 60 * 1000);
const pageViews = parseInt(variables.total_page_views) || 0;
const deals = parseInt(variables.associated_deals_count) || 0;
const engagementScore = Math.min(100, pageViews * 2 + deals * 20 + (variables.last_email_open ? 15 : 0));
output.next_touch_date = nextTouch.toISOString().split('T')[0];
output.engagement_score = engagementScore;
output.velocity_label = pacedays <= 3 ? 'fast' : pacedays <= 5 ? 'moderate' : 'standard';`,
            outputVariables: [
              { name: "next_touch_date", type: "string" },
              { name: "engagement_score", type: "number" },
              { name: "velocity_label", type: "string" },
            ],
          },
        },
      ),

      // 8. Sales alert condition
      n("8", "condition", "Check if sales team should be alerted", 1720, {
        conditionDetails: {
          conditions: [
            {
              variable: "should_alert_sales",
              operator: "equals",
              value: "yes",
            },
          ],
          logicalOperator: "AND",
        },
      }),

      // 9a. Conversion timing logger Sheets (True path — sales-relevant stages)
      sheetsNode(
        "9",
        "Log conversion timing and alert sales via Sheets",
        1960,
        "LifecycleTracking",
        `[["${new Date().toISOString()}", "{contact_email}", "{contact_name}", "{company_name}", "{transition_label}", "{content_type}", "{nurture_program}", "{engagement_score}", "{velocity_label}", "{next_touch_date}", "{lead_source}", "sales_alerted"]]`,
      ),

      // 9b. Conversion timing logger Sheets (False path — no sales alert needed)
      sheetsNode(
        "10",
        "Log conversion timing to tracking spreadsheet",
        1960,
        "LifecycleTracking",
        `[["${new Date().toISOString()}", "{contact_email}", "{contact_name}", "{company_name}", "{transition_label}", "{content_type}", "{nurture_program}", "{engagement_score}", "{velocity_label}", "{next_touch_date}", "{lead_source}", "no_alert"]]`,
        400,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9", "output_1"),
      e("8", "10", "output_2"),
    ],
  },

  // ===================================================================
  // 1549. LOST DEAL RE-ENGAGEMENT (8 nodes)
  // ===================================================================

  "Lost Deal Re-Engagement": {
    summary:
      "Receives a closed-lost deal webhook, AI-extracts deal details, loads full deal context from HubSpot, AI selects a win-back strategy, waits a 14-day cooling period, sends a personalized re-engagement email, checks for engagement response, and logs activity back to the CRM.",
    nodes: [
      trigger("Webhook: Deal marked as closed-lost", "webhook_app", {
        source: "hubspot",
        event: "deal.closed_lost",
        howItStarts:
          "Fires when a deal is moved to closed-lost stage in HubSpot CRM, sending the raw deal-change event with deal ID, associated contacts, loss reason, and deal properties",
        setup:
          "Configure HubSpot webhook for deal.propertyChange on dealstage to closed-lost. Ensure deal properties include close_lost_reason and competitor_name. Set up CRM re-engagement tracking.",
        inputFields: [],
      }),

      // 2. AI extract deal data
      aiNode(
        "2",
        "AI extract closed-lost deal details",
        280,
        "Extract closed-lost deal data from this raw webhook payload. Find the deal ID, deal name, deal amount, deal owner email, associated contact email, associated contact name, close date, loss reason, competitor mentioned, pipeline name, and deal stage history regardless of field naming. Return JSON: {deal_id, deal_name, deal_amount, owner_email, contact_email, contact_name, close_date, loss_reason, competitor, pipeline_name, stage_history}",
        "Raw webhook payload: {webhook_payload}",
        [
          { field: "deal_id", as: "deal_id", type: "string" },
          { field: "deal_name", as: "deal_name", type: "string" },
          { field: "deal_amount", as: "deal_amount", type: "string" },
          { field: "contact_email", as: "contact_email", type: "string" },
          { field: "contact_name", as: "contact_name", type: "string" },
          { field: "loss_reason", as: "loss_reason", type: "string" },
          { field: "competitor", as: "competitor_name", type: "string" },
          { field: "owner_email", as: "deal_owner_email", type: "string" },
          { field: "pipeline_name", as: "pipeline_name", type: "string" },
        ],
      ),

      // 3. Deal context assembler HubSpot
      hubspotGetNode(
        "3",
        "Load full deal context and engagement history",
        520,
        "crm/v3/objects/deals/{deal_id}?properties=dealname,amount,dealstage,closedate,hs_deal_stage_probability,pipeline,num_notes,num_contacted_notes,hs_analytics_source,hs_lastmodifieddate&associations=contacts",
        [
          {
            name: "properties.amount",
            customName: "deal_value",
            type: "string",
          },
          {
            name: "properties.hs_deal_stage_probability",
            customName: "last_probability",
            type: "string",
          },
          {
            name: "properties.num_notes",
            customName: "total_notes",
            type: "string",
          },
          {
            name: "properties.num_contacted_notes",
            customName: "contact_notes",
            type: "string",
          },
          {
            name: "properties.hs_analytics_source",
            customName: "deal_source",
            type: "string",
          },
        ],
      ),

      // 4. AI win-back strategy selector
      aiNode(
        "4",
        "AI select personalized win-back strategy",
        760,
        "You are a sales strategist specializing in deal recovery. Based on the lost deal context, determine the best win-back approach. Consider the loss reason, competitor involvement, deal value, engagement history, and time spent in pipeline. Choose one strategy: price_repositioning (if lost on price), feature_update (if lost on product gaps), relationship_rebuild (if lost on trust/relationship), competitive_displacement (if lost to specific competitor), timing_revisit (if budget/timing was the issue). Craft a personalized win-back message angle. Return JSON: {strategy, message_angle, subject_line, key_differentiator, recommended_offer, urgency_level, reopen_probability}",
        "Deal: {deal_name} | Value: ${deal_value} | Loss Reason: {loss_reason} | Competitor: {competitor_name} | Source: {deal_source} | Notes Count: {total_notes} | Last Probability: {last_probability}% | Pipeline: {pipeline_name}",
        [
          { field: "strategy", as: "winback_strategy", type: "string" },
          { field: "message_angle", as: "message_angle", type: "string" },
          { field: "subject_line", as: "winback_subject", type: "string" },
          {
            field: "key_differentiator",
            as: "key_differentiator",
            type: "string",
          },
          {
            field: "recommended_offer",
            as: "recommended_offer",
            type: "string",
          },
          { field: "urgency_level", as: "urgency_level", type: "string" },
          {
            field: "reopen_probability",
            as: "reopen_probability",
            type: "string",
          },
        ],
      ),

      // 5. Cooling period delay
      n(
        "5",
        "delayAction",
        "Wait 14-day cooling period before re-engagement",
        1000,
        {
          delayDetails: { duration: 14, unit: "days" },
        },
      ),

      // 6. Re-engagement email
      emailNode(
        "6",
        "Send personalized win-back re-engagement email",
        1240,
        "{contact_email}",
        "{winback_subject}",
        "Hi {contact_name},\\n\\nI hope things have been going well since we last spoke about {deal_name}.\\n\\n{message_angle}\\n\\nSince our last conversation, we have made some exciting developments that address the exact concerns you raised:\\n\\nKey Update: {key_differentiator}\\n\\nI would love to share more details. Would you be open to a quick 15-minute call this week?\\n\\n{recommended_offer}\\n\\nLooking forward to reconnecting.\\n\\nBest regards",
      ),

      // 7. Interest monitor condition
      n("7", "condition", "Check if deal had high reopen probability", 1480, {
        conditionDetails: {
          conditions: [
            { variable: "urgency_level", operator: "equals", value: "high" },
          ],
          logicalOperator: "AND",
        },
      }),

      // 8a. CRM activity logger HubSpot (True path — high urgency)
      hubspotPostNode(
        "8",
        "Log re-engagement activity and flag for follow-up",
        1720,
        "crm/v3/objects/deals/{deal_id}",
        [
          {
            key: "properties",
            value:
              '{"hs_lead_status":"RE_ENGAGED","notes_last_updated":"Re-engagement email sent. Strategy: {winback_strategy}. Reopen probability: {reopen_probability}. Urgency: HIGH. Follow-up required."}',
          },
        ],
        [
          { name: "id", customName: "updated_deal_id", type: "string" },
          {
            name: "properties.hs_lead_status",
            customName: "new_deal_status",
            type: "string",
          },
        ],
      ),

      // 8b. CRM activity logger HubSpot (False path — standard)
      hubspotPostNode(
        "9",
        "Log re-engagement email sent to CRM",
        1720,
        "crm/v3/objects/deals/{deal_id}",
        [
          {
            key: "properties",
            value:
              '{"notes_last_updated":"Win-back email sent. Strategy: {winback_strategy}. Reopen probability: {reopen_probability}. Standard follow-up."}',
          },
        ],
        [{ name: "id", customName: "logged_deal_id", type: "string" }],
        400,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
      e("7", "9", "output_2"),
    ],
  },

  // ===================================================================
  // 1550. LOST DEAL WIN-BACK CAMPAIGN (8 nodes)
  // ===================================================================

  "Lost Deal Win-Back Campaign": {
    summary:
      "Runs on a weekly schedule to scan HubSpot for recently closed-lost deals, enriches company data via Clearbit, AI generates personalized win-back messaging, sends the first outreach email, waits 7 days, branches on engagement, and alerts sales via Slack for engaged prospects.",
    nodes: [
      trigger("Schedule: Weekly closed-lost deal scanner", "schedule", {
        schedule: "Every Monday at 9:00 AM",
        howItStarts:
          "Runs weekly on Monday mornings to scan HubSpot for deals closed-lost in the past 30 days that have not yet received a win-back touch, initiating a systematic re-engagement campaign",
        setup:
          "Configure HubSpot API token with deal read/write permissions. Set up Clearbit API key for enrichment. Configure Slack webhook for sales channel notifications.",
        inputFields: [],
      }),

      // 2. HubSpot closed-lost deal scanner
      hubspotGetNode(
        "2",
        "Scan HubSpot for recent closed-lost deals",
        280,
        "crm/v3/objects/deals/search",
        [
          { name: "results", customName: "lost_deals", type: "string" },
          { name: "total", customName: "total_lost_deals", type: "number" },
          {
            name: "results[0].id",
            customName: "first_deal_id",
            type: "string",
          },
          {
            name: "results[0].properties.dealname",
            customName: "first_deal_name",
            type: "string",
          },
          {
            name: "results[0].properties.amount",
            customName: "first_deal_amount",
            type: "string",
          },
          {
            name: "results[0].properties.hs_closed_lost_reason",
            customName: "first_loss_reason",
            type: "string",
          },
          {
            name: "results[0].properties.hubspot_owner_id",
            customName: "first_owner_id",
            type: "string",
          },
        ],
      ),

      // 3. Data freshness refresher Clearbit API
      n("3", "hitApi", "Enrich company data via Clearbit for freshness", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://company.clearbit.com/v2/companies/find?domain={first_deal_name}",
          headers: [
            { key: "Authorization", value: "Bearer {clearbit_api_key}" },
          ],
          body: [],
          variables: [
            {
              name: "name",
              customName: "enriched_company_name",
              type: "string",
            },
            {
              name: "metrics.employees",
              customName: "company_employee_count",
              type: "string",
            },
            {
              name: "category.industry",
              customName: "company_industry",
              type: "string",
            },
            {
              name: "metrics.raised",
              customName: "company_funding",
              type: "string",
            },
            { name: "tech", customName: "company_tech_stack", type: "string" },
          ],
        },
      }),

      // 4. AI personalized win-back messaging
      aiNode(
        "4",
        "AI generate personalized win-back messaging",
        760,
        "You are an expert sales copywriter specializing in deal recovery. Given the lost deal details and enriched company data, craft a compelling and personalized win-back email. Address the specific loss reason, reference recent company changes (funding, growth, tech stack), and offer a concrete reason to reconsider. Be empathetic, not pushy. Return JSON: {subject_line, opening_hook, value_proposition, call_to_action, ps_line, tone_used}",
        "Deal: {first_deal_name} | Amount: ${first_deal_amount} | Loss Reason: {first_loss_reason} | Company: {enriched_company_name} | Employees: {company_employee_count} | Industry: {company_industry} | Funding: {company_funding} | Tech Stack: {company_tech_stack} | Total lost deals this batch: {total_lost_deals}",
        [
          { field: "subject_line", as: "wb_subject", type: "string" },
          { field: "opening_hook", as: "wb_opening", type: "string" },
          { field: "value_proposition", as: "wb_value_prop", type: "string" },
          { field: "call_to_action", as: "wb_cta", type: "string" },
          { field: "ps_line", as: "wb_ps", type: "string" },
          { field: "tone_used", as: "wb_tone", type: "string" },
        ],
      ),

      // 5. Touch 1 email
      emailNode(
        "5",
        "Send win-back touch 1 email",
        1000,
        "{first_deal_name}",
        "{wb_subject}",
        "{wb_opening}\\n\\n{wb_value_prop}\\n\\n{wb_cta}\\n\\nP.S. {wb_ps}",
      ),

      // 6. Delay 7 days
      n("6", "delayAction", "Wait 7 days for engagement response", 1240, {
        delayDetails: { duration: 7, unit: "days" },
      }),

      // 7. Engagement branch condition
      n(
        "7",
        "condition",
        "Check if total lost deals indicates high volume campaign",
        1480,
        {
          conditionDetails: {
            conditions: [
              {
                variable: "total_lost_deals",
                operator: "greater_than",
                value: "3",
              },
            ],
            logicalOperator: "AND",
          },
        },
      ),

      // 8a. Sales reactivation alert Slack (True — multi-deal campaign)
      slackNode(
        "8",
        "Alert sales team of win-back campaign results",
        1720,
        ":recycle: *Win-Back Campaign Launched*\\n>*Deals Targeted:* {total_lost_deals}\\n>*First Deal:* {first_deal_name} (${first_deal_amount})\\n>*Loss Reason:* {first_loss_reason}\\n>*Company:* {enriched_company_name} | {company_industry} | {company_employee_count} employees\\n>*Strategy Tone:* {wb_tone}\\n>*Touch 1 sent — monitoring for 7-day engagement window*\\n>\\n>Reps: watch for replies and schedule follow-ups for engaged prospects.",
      ),

      // 8b. Sales individual deal alert Slack (False — single deal)
      slackNode(
        "9",
        "Notify deal owner of single win-back attempt",
        1720,
        ":envelope: *Win-Back Email Sent*\\n>*Deal:* {first_deal_name} (${first_deal_amount})\\n>*Loss Reason:* {first_loss_reason}\\n>*Tone:* {wb_tone}\\n>Monitor for reply within 7 days.",
        400,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
      e("7", "9", "output_2"),
    ],
  },

  // ===================================================================
  // 1551. MQL NURTURE BY COMPANY SIZE (8 nodes)
  // ===================================================================

  "MQL Nurture by Company Size": {
    summary:
      "Receives an MQL-created webhook, AI-extracts lead data, segments by company size with runCode, AI selects size-relevant content, sends a targeted first touch email, waits 5 days, branches on engagement, and alerts the assigned SDR via Slack.",
    nodes: [
      trigger("Webhook: New MQL created in CRM", "webhook_app", {
        source: "hubspot",
        event: "contact.mql_created",
        howItStarts:
          "Fires when a contact reaches MQL status in HubSpot, sending the raw contact data including company size, industry, and lead score",
        setup:
          "Configure HubSpot webhook for contact.propertyChange on lifecyclestage to MQL. Prepare size-segmented content libraries. Set up SDR notification Slack channel.",
        inputFields: [],
      }),

      // 2. AI extract lead data
      aiNode(
        "2",
        "AI extract MQL lead details from webhook",
        280,
        "Extract MQL lead data from this raw webhook payload. Find the contact email, first name, last name, company name, company size (employee count), job title, lead score, lead source, phone number, and any product interest signals regardless of field naming format. Return JSON: {email, first_name, last_name, company_name, company_size, job_title, lead_score, lead_source, phone, product_interest}",
        "Raw webhook payload: {webhook_payload}",
        [
          { field: "email", as: "lead_email", type: "string" },
          { field: "first_name", as: "lead_first_name", type: "string" },
          { field: "last_name", as: "lead_last_name", type: "string" },
          { field: "company_name", as: "lead_company", type: "string" },
          { field: "company_size", as: "company_size", type: "string" },
          { field: "job_title", as: "lead_title", type: "string" },
          { field: "lead_score", as: "lead_score", type: "string" },
          { field: "lead_source", as: "lead_source", type: "string" },
          { field: "product_interest", as: "product_interest", type: "string" },
        ],
      ),

      // 3. Company size segmenter runCode
      n("3", "runCode", "Segment lead by company size tier", 520, {
        runCodeDetails: {
          code: `const size = parseInt(variables.company_size) || 0;
let tier, content_track, sdr_priority, typical_deal_size;
if (size <= 50) {
  tier = 'smb'; content_track = 'quick_roi_stories'; sdr_priority = 'standard'; typical_deal_size = '5k-15k';
} else if (size <= 200) {
  tier = 'mid_market'; content_track = 'scalability_case_studies'; sdr_priority = 'high'; typical_deal_size = '15k-50k';
} else if (size <= 1000) {
  tier = 'enterprise'; content_track = 'enterprise_security_compliance'; sdr_priority = 'high'; typical_deal_size = '50k-200k';
} else {
  tier = 'strategic'; content_track = 'executive_transformation'; sdr_priority = 'critical'; typical_deal_size = '200k+';
}
output.size_tier = tier;
output.content_track = content_track;
output.sdr_priority = sdr_priority;
output.typical_deal_size = typical_deal_size;
output.size_label = size + ' employees (' + tier.replace('_',' ') + ')';`,
          outputVariables: [
            { name: "size_tier", type: "string" },
            { name: "content_track", type: "string" },
            { name: "sdr_priority", type: "string" },
            { name: "typical_deal_size", type: "string" },
            { name: "size_label", type: "string" },
          ],
        },
      }),

      // 4. AI size-relevant content selector
      aiNode(
        "4",
        "AI select size-appropriate nurture content",
        760,
        "You are a demand gen specialist. Given the MQL lead details and their company size tier, select the most relevant first-touch content asset. For SMB: focus on quick wins, ease of use, fast ROI. For mid-market: focus on scalability, team adoption, growth stories. For enterprise: focus on security, compliance, integration breadth. For strategic: focus on digital transformation, executive sponsorship, global deployments. Return JSON: {asset_title, asset_description, asset_url_path, personalized_hook, value_prop_for_size, suggested_next_asset}",
        "Lead: {lead_first_name} {lead_last_name} | Title: {lead_title} | Company: {lead_company} | Size: {size_label} | Tier: {size_tier} | Content Track: {content_track} | Product Interest: {product_interest} | Lead Score: {lead_score}",
        [
          { field: "asset_title", as: "content_title", type: "string" },
          {
            field: "asset_description",
            as: "content_description",
            type: "string",
          },
          { field: "asset_url_path", as: "content_url", type: "string" },
          { field: "personalized_hook", as: "email_hook", type: "string" },
          {
            field: "value_prop_for_size",
            as: "size_value_prop",
            type: "string",
          },
          { field: "suggested_next_asset", as: "next_content", type: "string" },
        ],
      ),

      // 5. Touch 1 email
      emailNode(
        "5",
        "Send size-segmented nurture touch 1 email",
        1000,
        "{lead_email}",
        "{content_title} - Built for teams like {lead_company}",
        "Hi {lead_first_name},\\n\\n{email_hook}\\n\\nAs a {size_label} company, {size_value_prop}\\n\\nI wanted to share this resource that is especially relevant for your team:\\n\\n{content_title}\\n{content_description}\\n\\nCheck it out here: {content_url}\\n\\nLooking forward to helping {lead_company} succeed.\\n\\nBest regards",
      ),

      // 6. Delay 5 days
      n("6", "delayAction", "Wait 5 days for engagement signal", 1240, {
        delayDetails: { duration: 5, unit: "days" },
      }),

      // 7. Engagement check condition
      n(
        "7",
        "condition",
        "Check if SDR priority warrants immediate alert",
        1480,
        {
          conditionDetails: {
            conditions: [
              {
                variable: "sdr_priority",
                operator: "not_equals",
                value: "standard",
              },
            ],
            logicalOperator: "AND",
          },
        },
      ),

      // 8a. SDR briefing Slack (True — high/critical priority)
      slackNode(
        "8",
        "Send high-priority MQL briefing to SDR team",
        1720,
        ":dart: *High-Priority MQL — {size_tier} Segment*\\n>*Lead:* {lead_first_name} {lead_last_name} ({lead_title})\\n>*Company:* {lead_company} | {size_label}\\n>*Typical Deal Size:* {typical_deal_size}\\n>*Lead Score:* {lead_score} | Source: {lead_source}\\n>*Interest:* {product_interest}\\n>*Content Sent:* {content_title}\\n>*Priority:* {sdr_priority}\\n>\\n>Please follow up within 24 hours. Next suggested asset: {next_content}",
      ),

      // 8b. SDR briefing Slack (False — standard priority)
      slackNode(
        "9",
        "Log standard MQL to SDR queue",
        1720,
        ":clipboard: *New MQL — SMB Segment*\\n>*Lead:* {lead_first_name} {lead_last_name} ({lead_title})\\n>*Company:* {lead_company} | {size_label}\\n>*Lead Score:* {lead_score} | Source: {lead_source}\\n>*Content Sent:* {content_title}\\n>Follow up at standard cadence.",
        400,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
      e("7", "9", "output_2"),
    ],
  },

  // ===================================================================
  // 1552. MQL NURTURE BY INDUSTRY (8 nodes)
  // ===================================================================

  "MQL Nurture by Industry": {
    summary:
      "Receives a webhook when industry data is identified for an MQL, AI-extracts lead details, enriches industry data via Clearbit, AI selects industry-specific content assets, sends an industry-tailored nurture email, waits 5 days, branches on engagement, and routes to an industry-savvy seller in HubSpot.",
    nodes: [
      trigger("Webhook: MQL with industry data identified", "webhook_app", {
        source: "hubspot",
        event: "contact.industry_identified",
        howItStarts:
          "Fires when an MQL contact's industry field is populated or updated in HubSpot, sending the raw contact data with industry classification and lead details",
        setup:
          "Configure HubSpot webhook for contact.propertyChange on industry property. Set up Clearbit API for industry enrichment. Prepare industry-specific content libraries and seller routing rules.",
        inputFields: [],
      }),

      // 2. AI extract lead data
      aiNode(
        "2",
        "AI extract MQL and industry details from webhook",
        280,
        "Extract MQL lead and industry data from this raw webhook payload. Find the contact email, first name, last name, company name, identified industry, sub-industry, job title, lead score, company domain, and any content engagement history regardless of field naming. Return JSON: {email, first_name, last_name, company_name, industry, sub_industry, job_title, lead_score, company_domain, content_history}",
        "Raw webhook payload: {webhook_payload}",
        [
          { field: "email", as: "lead_email", type: "string" },
          { field: "first_name", as: "lead_first_name", type: "string" },
          { field: "last_name", as: "lead_last_name", type: "string" },
          { field: "company_name", as: "lead_company", type: "string" },
          { field: "industry", as: "lead_industry", type: "string" },
          { field: "sub_industry", as: "lead_sub_industry", type: "string" },
          { field: "job_title", as: "lead_title", type: "string" },
          { field: "lead_score", as: "lead_score", type: "string" },
          { field: "company_domain", as: "company_domain", type: "string" },
        ],
      ),

      // 3. Industry enrichment Clearbit API
      n("3", "hitApi", "Enrich company industry data via Clearbit", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://company.clearbit.com/v2/companies/find?domain={company_domain}",
          headers: [
            { key: "Authorization", value: "Bearer {clearbit_api_key}" },
          ],
          body: [],
          variables: [
            {
              name: "category.industry",
              customName: "enriched_industry",
              type: "string",
            },
            {
              name: "category.subIndustry",
              customName: "enriched_sub_industry",
              type: "string",
            },
            {
              name: "category.industryGroup",
              customName: "industry_group",
              type: "string",
            },
            {
              name: "metrics.employees",
              customName: "employee_count",
              type: "string",
            },
            { name: "tags", customName: "company_tags", type: "string" },
          ],
        },
      }),

      // 4. AI industry-specific asset selector
      aiNode(
        "4",
        "AI select industry-specific content and messaging",
        760,
        "You are an industry marketing specialist. Given the MQL details and enriched industry data, select the most compelling industry-specific content. Reference real industry challenges: Healthcare (HIPAA, patient engagement), Financial Services (compliance, fraud detection), Technology (scalability, DevOps), Manufacturing (supply chain, IoT), Retail (omnichannel, inventory). Craft messaging that speaks the industry language. Return JSON: {industry_asset_title, industry_challenge, our_solution_angle, customer_proof_point, subject_line, industry_cta}",
        "Lead: {lead_first_name} {lead_last_name} | Title: {lead_title} | Company: {lead_company} | Industry: {enriched_industry} | Sub-industry: {enriched_sub_industry} | Group: {industry_group} | Size: {employee_count} | Tags: {company_tags} | Lead Score: {lead_score}",
        [
          {
            field: "industry_asset_title",
            as: "ind_asset_title",
            type: "string",
          },
          { field: "industry_challenge", as: "ind_challenge", type: "string" },
          { field: "our_solution_angle", as: "ind_solution", type: "string" },
          { field: "customer_proof_point", as: "ind_proof", type: "string" },
          { field: "subject_line", as: "ind_subject", type: "string" },
          { field: "industry_cta", as: "ind_cta", type: "string" },
        ],
      ),

      // 5. Industry nurture email
      emailNode(
        "5",
        "Send industry-tailored nurture email",
        1000,
        "{lead_email}",
        "{ind_subject}",
        "Hi {lead_first_name},\\n\\nWorking in {enriched_industry}, you are likely familiar with this challenge: {ind_challenge}\\n\\nAt {lead_company}, this probably shows up in your day-to-day as a {lead_title}.\\n\\nHere is how we help: {ind_solution}\\n\\nDo not just take our word for it — {ind_proof}\\n\\nI put together a resource specifically for {enriched_industry} professionals:\\n{ind_asset_title}\\n\\n{ind_cta}\\n\\nBest regards",
      ),

      // 6. Delay 5 days
      n("6", "delayAction", "Wait 5 days for engagement tracking", 1240, {
        delayDetails: { duration: 5, unit: "days" },
      }),

      // 7. Engagement branch condition
      n(
        "7",
        "condition",
        "Check if lead score indicates high engagement",
        1480,
        {
          conditionDetails: {
            conditions: [
              { variable: "lead_score", operator: "greater_than", value: "50" },
            ],
            logicalOperator: "AND",
          },
        },
      ),

      // 8a. Industry-savvy seller routing HubSpot (True — high score)
      hubspotPostNode(
        "8",
        "Route to industry-specialist seller in HubSpot",
        1720,
        "crm/v3/objects/contacts/{lead_email}",
        [
          {
            key: "properties",
            value:
              '{"hs_lead_status":"INDUSTRY_QUALIFIED","industry":"{enriched_industry}","notes_last_updated":"Industry nurture engaged. Score: {lead_score}. Challenge: {ind_challenge}. Solution angle: {ind_solution}. Ready for industry-specialist seller."}',
          },
        ],
        [
          { name: "id", customName: "routed_contact_id", type: "string" },
          {
            name: "properties.hs_lead_status",
            customName: "updated_lead_status",
            type: "string",
          },
        ],
      ),

      // 8b. Standard nurture continuation (False — lower score)
      hubspotPostNode(
        "9",
        "Update contact with industry nurture status",
        1720,
        "crm/v3/objects/contacts/{lead_email}",
        [
          {
            key: "properties",
            value:
              '{"notes_last_updated":"Industry nurture touch 1 sent. Industry: {enriched_industry}. Score: {lead_score}. Continuing standard nurture cadence."}',
          },
        ],
        [{ name: "id", customName: "nurture_contact_id", type: "string" }],
        400,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
      e("7", "9", "output_2"),
    ],
  },

  // ===================================================================
  // 1553. MQL NURTURE BY PERSONA (9 nodes)
  // ===================================================================

  "MQL Nurture by Persona": {
    summary:
      "Receives a webhook when a persona is detected for an MQL, AI-extracts lead data, classifies the buyer persona with runCode, AI maps persona-specific content, sends persona touch 1 email, waits 5 days, branches on engagement, sends persona touch 2 email for engaged leads, and hands off to CRM for the rest.",
    nodes: [
      trigger("Webhook: MQL buyer persona detected", "webhook_app", {
        source: "hubspot",
        event: "contact.persona_detected",
        howItStarts:
          "Fires when a buyer persona is identified for an MQL contact through form fills, behavior scoring, or enrichment data, sending the raw persona detection payload",
        setup:
          "Configure HubSpot webhook for contact.propertyChange on hs_persona property. Define persona-to-content mappings. Set up persona-based SDR routing rules.",
        inputFields: [],
      }),

      // 2. AI extract lead data
      aiNode(
        "2",
        "AI extract lead and persona signals from webhook",
        280,
        "Extract MQL lead data with persona signals from this raw webhook payload. Find the contact email, first name, last name, company name, job title, seniority level, department, detected persona label, lead score, recent page views, and content downloads regardless of field naming. Return JSON: {email, first_name, last_name, company_name, job_title, seniority, department, persona_label, lead_score, recent_pages, downloads}",
        "Raw webhook payload: {webhook_payload}",
        [
          { field: "email", as: "lead_email", type: "string" },
          { field: "first_name", as: "lead_first_name", type: "string" },
          { field: "last_name", as: "lead_last_name", type: "string" },
          { field: "company_name", as: "lead_company", type: "string" },
          { field: "job_title", as: "lead_title", type: "string" },
          { field: "seniority", as: "lead_seniority", type: "string" },
          { field: "department", as: "lead_department", type: "string" },
          { field: "persona_label", as: "detected_persona", type: "string" },
          { field: "lead_score", as: "lead_score", type: "string" },
          { field: "downloads", as: "content_downloads", type: "string" },
        ],
      ),

      // 3. Persona classifier runCode
      n("3", "runCode", "Classify and enrich buyer persona", 520, {
        runCodeDetails: {
          code: `const title = (variables.lead_title || '').toLowerCase();
const seniority = (variables.lead_seniority || '').toLowerCase();
const dept = (variables.lead_department || '').toLowerCase();
const detected = (variables.detected_persona || '').toLowerCase();
let persona, pain_points, buying_role, content_preference;
if (detected.includes('executive') || seniority.includes('c-level') || title.match(/^(ceo|cto|cfo|coo|vp|svp|evp)/)) {
  persona = 'executive_buyer'; pain_points = 'ROI, strategic alignment, competitive advantage'; buying_role = 'decision_maker'; content_preference = 'executive_briefs';
} else if (dept.includes('marketing') || title.match(/(marketing|demand|growth)/)) {
  persona = 'marketing_leader'; pain_points = 'pipeline generation, attribution, campaign efficiency'; buying_role = 'champion'; content_preference = 'case_studies_roi';
} else if (dept.includes('sales') || title.match(/(sales|revenue|account)/)) {
  persona = 'sales_leader'; pain_points = 'quota attainment, pipeline velocity, rep productivity'; buying_role = 'influencer'; content_preference = 'product_demos';
} else if (dept.includes('it') || dept.includes('engineering') || title.match(/(engineer|developer|architect|devops)/)) {
  persona = 'technical_evaluator'; pain_points = 'integration, security, scalability, API quality'; buying_role = 'evaluator'; content_preference = 'technical_docs';
} else {
  persona = 'operations_user'; pain_points = 'workflow efficiency, time savings, ease of use'; buying_role = 'end_user'; content_preference = 'how_to_guides';
}
output.classified_persona = persona;
output.persona_pain_points = pain_points;
output.buying_role = buying_role;
output.content_preference = content_preference;`,
          outputVariables: [
            { name: "classified_persona", type: "string" },
            { name: "persona_pain_points", type: "string" },
            { name: "buying_role", type: "string" },
            { name: "content_preference", type: "string" },
          ],
        },
      }),

      // 4. AI persona content mapper
      aiNode(
        "4",
        "AI select persona-aligned content journey",
        760,
        "You are a persona-based marketing strategist. Given the classified buyer persona, their pain points, and buying role, select the ideal two-touch content journey. Touch 1 should be educational and trust-building. Touch 2 should be more solution-oriented and drive to a conversion action. Return JSON: {touch1_title, touch1_description, touch1_subject, touch2_title, touch2_description, touch2_subject, persona_greeting_style, conversion_action}",
        "Persona: {classified_persona} | Pain Points: {persona_pain_points} | Buying Role: {buying_role} | Preference: {content_preference} | Title: {lead_title} | Company: {lead_company} | Downloads: {content_downloads} | Score: {lead_score}",
        [
          { field: "touch1_title", as: "t1_title", type: "string" },
          { field: "touch1_description", as: "t1_desc", type: "string" },
          { field: "touch1_subject", as: "t1_subject", type: "string" },
          { field: "touch2_title", as: "t2_title", type: "string" },
          { field: "touch2_description", as: "t2_desc", type: "string" },
          { field: "touch2_subject", as: "t2_subject", type: "string" },
          {
            field: "persona_greeting_style",
            as: "greeting_style",
            type: "string",
          },
          {
            field: "conversion_action",
            as: "conversion_action",
            type: "string",
          },
        ],
      ),

      // 5. Persona touch 1 email
      emailNode(
        "5",
        "Send persona-aligned touch 1 email",
        1000,
        "{lead_email}",
        "{t1_subject}",
        "{greeting_style} {lead_first_name},\\n\\nAs a {lead_title} at {lead_company}, you are probably navigating challenges around {persona_pain_points}.\\n\\nWe have put together a resource that addresses exactly that:\\n\\n{t1_title}\\n{t1_desc}\\n\\nThis was specifically created for professionals in your role who need practical insights they can act on immediately.\\n\\nBest regards",
      ),

      // 6. Delay 5 days
      n("6", "delayAction", "Wait 5 days before touch 2", 1240, {
        delayDetails: { duration: 5, unit: "days" },
      }),

      // 7. Engagement monitor condition
      n("7", "condition", "Check if lead score qualifies for touch 2", 1480, {
        conditionDetails: {
          conditions: [
            { variable: "lead_score", operator: "greater_than", value: "40" },
          ],
          logicalOperator: "AND",
        },
      }),

      // 8. Persona touch 2 email (True — engaged)
      emailNode(
        "8",
        "Send persona touch 2 conversion email",
        1720,
        "{lead_email}",
        "{t2_subject}",
        "Hi {lead_first_name},\\n\\nLast week I shared {t1_title} — I hope you found it useful.\\n\\nToday I wanted to go a step further with something more hands-on:\\n\\n{t2_title}\\n{t2_desc}\\n\\nMany {classified_persona}s in similar roles have found this is the turning point where things click.\\n\\n{conversion_action}\\n\\nLooking forward to hearing your thoughts.\\n\\nBest regards",
      ),

      // 9. CRM handoff HubSpot (False — not engaged, update CRM)
      hubspotPostNode(
        "9",
        "Update CRM with persona nurture status",
        1720,
        "crm/v3/objects/contacts/{lead_email}",
        [
          {
            key: "properties",
            value:
              '{"hs_persona":"{classified_persona}","notes_last_updated":"Persona nurture: {classified_persona}. Touch 1 sent. Score: {lead_score}. Buying role: {buying_role}. Below engagement threshold — continuing drip."}',
          },
        ],
        [{ name: "id", customName: "crm_contact_id", type: "string" }],
        400,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
      e("7", "9", "output_2"),
    ],
  },

  // ===================================================================
  // 1554. MQL NURTURE BY PRODUCT INTEREST (8 nodes)
  // ===================================================================

  "MQL Nurture by Product Interest": {
    summary:
      "Receives a webhook when product interest is detected, AI-extracts interest signals, aggregates product intent with runCode, AI builds a product-specific nurture path, sends a targeted first touch email, waits 5 days, validates interest with a condition, and sends a demo invitation email for validated leads.",
    nodes: [
      trigger("Webhook: Product interest signal detected", "webhook_app", {
        source: "hubspot",
        event: "contact.product_interest",
        howItStarts:
          "Fires when a contact's product interest is identified through page visits, form submissions, or content downloads, sending the raw interest signal payload",
        setup:
          "Configure HubSpot webhook for contact.propertyChange on product_interest or hs_analytics_last_url properties. Prepare product-specific content assets. Define product demo booking links.",
        inputFields: [],
      }),

      // 2. AI extract interest data
      aiNode(
        "2",
        "AI extract product interest signals from webhook",
        280,
        "Extract product interest data from this raw webhook payload. Find the contact email, first name, last name, company name, job title, product pages visited, content downloaded, form submitted, product category interested in, lead score, and referral source regardless of field naming. Return JSON: {email, first_name, last_name, company_name, job_title, pages_visited, content_downloaded, form_name, product_category, lead_score, referral_source}",
        "Raw webhook payload: {webhook_payload}",
        [
          { field: "email", as: "lead_email", type: "string" },
          { field: "first_name", as: "lead_first_name", type: "string" },
          { field: "last_name", as: "lead_last_name", type: "string" },
          { field: "company_name", as: "lead_company", type: "string" },
          { field: "job_title", as: "lead_title", type: "string" },
          { field: "product_category", as: "product_interest", type: "string" },
          { field: "pages_visited", as: "pages_visited", type: "string" },
          {
            field: "content_downloaded",
            as: "content_downloaded",
            type: "string",
          },
          { field: "lead_score", as: "lead_score", type: "string" },
          { field: "referral_source", as: "referral_source", type: "string" },
        ],
      ),

      // 3. Product intent aggregator runCode
      n("3", "runCode", "Aggregate and score product intent signals", 520, {
        runCodeDetails: {
          code: `const product = (variables.product_interest || '').toLowerCase();
const pages = (variables.pages_visited || '').toLowerCase();
const downloads = (variables.content_downloaded || '').toLowerCase();
let intent_strength = 0;
if (pages.includes('pricing')) intent_strength += 30;
if (pages.includes('demo')) intent_strength += 25;
if (pages.includes('comparison')) intent_strength += 20;
if (pages.includes('features')) intent_strength += 15;
if (downloads.includes('whitepaper')) intent_strength += 10;
if (downloads.includes('case_study')) intent_strength += 15;
if (downloads.includes('datasheet')) intent_strength += 20;
const score = parseInt(variables.lead_score) || 0;
intent_strength = Math.min(100, intent_strength + Math.floor(score / 5));
let intent_tier = intent_strength >= 70 ? 'hot' : intent_strength >= 40 ? 'warm' : 'exploring';
output.intent_score = intent_strength;
output.intent_tier = intent_tier;
output.primary_product = product || 'general_platform';
output.demo_ready = intent_strength >= 60 ? 'yes' : 'no';
output.interest_summary = 'Product: ' + (product || 'general') + ' | Pages: ' + (pages || 'none') + ' | Downloads: ' + (downloads || 'none') + ' | Intent: ' + intent_tier + ' (' + intent_strength + ')';`,
          outputVariables: [
            { name: "intent_score", type: "number" },
            { name: "intent_tier", type: "string" },
            { name: "primary_product", type: "string" },
            { name: "demo_ready", type: "string" },
            { name: "interest_summary", type: "string" },
          ],
        },
      }),

      // 4. AI product nurture path builder
      aiNode(
        "4",
        "AI build product-specific nurture journey",
        760,
        "You are a product marketing specialist. Given the MQL lead and their product interest signals, build a tailored nurture journey. Match the primary product interest with the most compelling content: product overview, feature deep-dives, comparison guides, customer success stories, and ROI calculators specific to that product. Adjust messaging based on intent tier (hot=direct, warm=educational, exploring=awareness). Return JSON: {email_subject, opening_personalization, product_highlight, social_proof_snippet, resource_link_label, resource_description, demo_cta_text}",
        "Lead: {lead_first_name} {lead_last_name} | Title: {lead_title} | Company: {lead_company} | Product: {primary_product} | Intent: {intent_tier} ({intent_score}) | {interest_summary}",
        [
          { field: "email_subject", as: "prod_subject", type: "string" },
          {
            field: "opening_personalization",
            as: "prod_opening",
            type: "string",
          },
          { field: "product_highlight", as: "prod_highlight", type: "string" },
          { field: "social_proof_snippet", as: "prod_proof", type: "string" },
          {
            field: "resource_link_label",
            as: "prod_resource_label",
            type: "string",
          },
          {
            field: "resource_description",
            as: "prod_resource_desc",
            type: "string",
          },
          { field: "demo_cta_text", as: "demo_cta", type: "string" },
        ],
      ),

      // 5. Touch 1 email
      emailNode(
        "5",
        "Send product-specific nurture touch 1 email",
        1000,
        "{lead_email}",
        "{prod_subject}",
        "Hi {lead_first_name},\\n\\n{prod_opening}\\n\\n{prod_highlight}\\n\\n{prod_proof}\\n\\nHere is a resource I think you will find valuable:\\n{prod_resource_label}: {prod_resource_desc}\\n\\nBest regards",
      ),

      // 6. Delay 5 days
      n("6", "delayAction", "Wait 5 days for interest validation", 1240, {
        delayDetails: { duration: 5, unit: "days" },
      }),

      // 7. Interest validation condition
      n("7", "condition", "Check if lead is demo-ready based on intent", 1480, {
        conditionDetails: {
          conditions: [
            { variable: "demo_ready", operator: "equals", value: "yes" },
          ],
          logicalOperator: "AND",
        },
      }),

      // 8a. Demo invite email (True — demo-ready)
      emailNode(
        "8",
        "Send personalized demo invitation email",
        1720,
        "{lead_email}",
        "See {primary_product} in action - personalized demo for {lead_company}",
        "Hi {lead_first_name},\\n\\nBased on your interest in {primary_product}, I would love to show you exactly how it can work for {lead_company}.\\n\\n{demo_cta}\\n\\nIn just 20 minutes, I can walk you through:\\n- How {prod_highlight} applies to your specific use case\\n- Live examples from companies similar to yours\\n- A personalized ROI estimate for your team\\n\\nBook a time that works: [Demo Calendar Link]\\n\\nLooking forward to connecting.\\n\\nBest regards",
      ),

      // 8b. Continue nurture (False — not demo-ready)
      emailNode(
        "9",
        "Send additional educational content email",
        1720,
        "{lead_email}",
        "More resources on {primary_product} for {lead_company}",
        "Hi {lead_first_name},\\n\\nI hope you enjoyed the resource I shared earlier about {primary_product}.\\n\\nHere is another piece that dives deeper into {prod_resource_desc}\\n\\nMany teams at companies like yours have found this helpful as they evaluate their options.\\n\\nFeel free to reach out if you have any questions.\\n\\nBest regards",
        400,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
      e("7", "9", "output_2"),
    ],
  },

  // ===================================================================
  // 1555. MARKETO LEAD LIFECYCLE STAGE MANAGER (9 nodes)
  // ===================================================================

  "Marketo Lead Lifecycle Stage Manager": {
    summary:
      "Receives Marketo activity webhooks, AI-extracts activity data, evaluates transition rules with runCode, checks prerequisites with a condition gate, updates Marketo stage via API, syncs lifecycle to HubSpot CRM, enrolls in the appropriate Marketo program, alerts sales via Slack, and logs the audit entry to Sheets.",
    nodes: [
      trigger("Webhook: Marketo lead activity received", "webhook_app", {
        source: "marketo",
        event: "lead.activity_logged",
        howItStarts:
          "Fires when Marketo sends a lead activity webhook (form fill, email click, page visit, score change), carrying the raw activity payload with lead ID, activity type, and metadata",
        setup:
          "Configure Marketo webhook to POST activity events. Set up Marketo REST API credentials (client_id, client_secret). Configure HubSpot integration for CRM sync. Prepare Sheets for audit logging.",
        inputFields: [],
      }),

      // 2. AI extract activity data
      aiNode(
        "2",
        "AI extract Marketo activity details from webhook",
        280,
        "Extract Marketo lead activity data from this raw webhook payload. Find the lead ID, lead email, first name, last name, activity type, activity date, current lead status, current score, campaign name, program name, and any associated attributes regardless of Marketo field naming. Return JSON: {lead_id, email, first_name, last_name, activity_type, activity_date, current_status, current_score, campaign_name, program_name, attributes}",
        "Raw webhook payload: {webhook_payload}",
        [
          { field: "lead_id", as: "mkto_lead_id", type: "string" },
          { field: "email", as: "mkto_email", type: "string" },
          { field: "first_name", as: "mkto_first_name", type: "string" },
          { field: "last_name", as: "mkto_last_name", type: "string" },
          { field: "activity_type", as: "mkto_activity_type", type: "string" },
          {
            field: "current_status",
            as: "mkto_current_status",
            type: "string",
          },
          { field: "current_score", as: "mkto_score", type: "string" },
          { field: "campaign_name", as: "mkto_campaign", type: "string" },
          { field: "program_name", as: "mkto_program", type: "string" },
        ],
      ),

      // 3. Transition rules evaluator runCode
      n("3", "runCode", "Evaluate lifecycle stage transition rules", 520, {
        runCodeDetails: {
          code: `const status = (variables.mkto_current_status || '').toLowerCase();
const score = parseInt(variables.mkto_score) || 0;
const activity = (variables.mkto_activity_type || '').toLowerCase();
let new_stage = status;
let transition_allowed = false;
let prerequisite = 'none';
const rules = {
  'anonymous': { next: 'known', min_score: 0, requires: 'email_captured' },
  'known': { next: 'engaged', min_score: 10, requires: 'activity_count_3' },
  'engaged': { next: 'mql', min_score: 50, requires: 'fit_score_check' },
  'mql': { next: 'sal', min_score: 70, requires: 'sales_accepted' },
  'sal': { next: 'sql', min_score: 80, requires: 'opportunity_created' },
  'sql': { next: 'opportunity', min_score: 80, requires: 'deal_stage_check' },
};
const rule = rules[status];
if (rule && score >= rule.min_score) {
  new_stage = rule.next;
  transition_allowed = true;
  prerequisite = rule.requires;
}
output.proposed_stage = new_stage;
output.transition_allowed = transition_allowed ? 'yes' : 'no';
output.prerequisite_check = prerequisite;
output.score_threshold_met = score >= (rule ? rule.min_score : 999) ? 'yes' : 'no';
output.transition_summary = status + ' → ' + new_stage + ' (score: ' + score + ', allowed: ' + transition_allowed + ')';`,
          outputVariables: [
            { name: "proposed_stage", type: "string" },
            { name: "transition_allowed", type: "string" },
            { name: "prerequisite_check", type: "string" },
            { name: "score_threshold_met", type: "string" },
            { name: "transition_summary", type: "string" },
          ],
        },
      }),

      // 4. Prerequisite checker condition
      n("4", "condition", "Check if transition prerequisites are met", 760, {
        conditionDetails: {
          conditions: [
            {
              variable: "transition_allowed",
              operator: "equals",
              value: "yes",
            },
          ],
          logicalOperator: "AND",
        },
      }),

      // 5. Marketo stage updater API (True path)
      n("5", "hitApi", "Update lead lifecycle stage in Marketo", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://{marketo_instance}.mktorest.com/rest/v1/leads.json",
          headers: [
            { key: "Authorization", value: "Bearer {marketo_access_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "action", value: "updateOnly" },
            {
              key: "input",
              value:
                '[{"id":"{mkto_lead_id}","leadStatus":"{proposed_stage}"}]',
            },
          ],
          variables: [
            {
              name: "result[0].status",
              customName: "mkto_update_status",
              type: "string",
            },
            {
              name: "result[0].id",
              customName: "mkto_updated_id",
              type: "string",
            },
          ],
        },
      }),

      // 6. CRM lifecycle sync HubSpot
      hubspotPostNode(
        "6",
        "Sync lifecycle stage to HubSpot CRM",
        1240,
        "crm/v3/objects/contacts/{mkto_email}",
        [
          {
            key: "properties",
            value:
              '{"lifecyclestage":"{proposed_stage}","hs_lead_status":"{proposed_stage}","notes_last_updated":"Marketo stage transition: {transition_summary}. Activity: {mkto_activity_type}. Campaign: {mkto_campaign}."}',
          },
        ],
        [
          { name: "id", customName: "hs_synced_contact_id", type: "string" },
          {
            name: "properties.lifecyclestage",
            customName: "hs_synced_stage",
            type: "string",
          },
        ],
      ),

      // 7. Program enrollment API
      n("7", "hitApi", "Enroll lead in appropriate Marketo program", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://{marketo_instance}.mktorest.com/rest/v1/leads/programs/{mkto_program}/status.json",
          headers: [
            { key: "Authorization", value: "Bearer {marketo_access_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "input",
              value: '[{"id":"{mkto_lead_id}","status":"{proposed_stage}"}]',
            },
          ],
          variables: [
            {
              name: "result[0].status",
              customName: "enrollment_status",
              type: "string",
            },
          ],
        },
      }),

      // 8. Sales alert Slack
      slackNode(
        "8",
        "Alert sales of lifecycle stage progression",
        1720,
        ":arrow_up: *Marketo Stage Transition*\\n>*Lead:* {mkto_first_name} {mkto_last_name} ({mkto_email})\\n>*Transition:* {transition_summary}\\n>*Activity Trigger:* {mkto_activity_type}\\n>*Campaign:* {mkto_campaign}\\n>*Score:* {mkto_score}\\n>*HubSpot Synced:* {hs_synced_stage}\\n>*Program Enrollment:* {enrollment_status}",
      ),

      // 9. Audit entry logger Sheets
      sheetsNode(
        "9",
        "Log lifecycle transition audit entry to Sheets",
        1960,
        "LifecycleAudit",
        `[["${new Date().toISOString()}", "{mkto_lead_id}", "{mkto_email}", "{mkto_current_status}", "{proposed_stage}", "{mkto_score}", "{mkto_activity_type}", "{mkto_campaign}", "{mkto_update_status}", "{hs_synced_stage}", "{enrollment_status}"]]`,
      ),

      // 4b. Audit log for blocked transitions (False path)
      sheetsNode(
        "10",
        "Log blocked transition attempt to audit sheet",
        760,
        "LifecycleAudit",
        `[["${new Date().toISOString()}", "{mkto_lead_id}", "{mkto_email}", "{mkto_current_status}", "BLOCKED", "{mkto_score}", "{mkto_activity_type}", "{prerequisite_check}", "transition_not_allowed", "", ""]]`,
        400,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "10", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 1556. MARKETO LEAD SCORING AUTOMATION (8 nodes)
  // ===================================================================

  "Marketo Lead Scoring Automation": {
    summary:
      "Receives engagement event webhooks, AI-extracts event data, calculates demographic score with runCode, calculates behavioral score with runCode, aggregates scores with decay logic, checks MQL threshold condition, writes final score to Marketo API, and logs the scoring outcome to Sheets.",
    nodes: [
      trigger("Webhook: Lead engagement event from Marketo", "webhook_app", {
        source: "marketo",
        event: "lead.engagement_event",
        howItStarts:
          "Fires when Marketo detects a lead engagement event (email open, link click, form fill, web visit, content download), sending the raw event payload with lead and activity details",
        setup:
          "Configure Marketo smart campaign to send webhook on engagement events. Set up Marketo REST API for score writing. Define demographic and behavioral scoring models. Prepare Sheets for score history.",
        inputFields: [],
      }),

      // 2. AI extract event data
      aiNode(
        "2",
        "AI extract engagement event details from webhook",
        280,
        "Extract engagement event data from this raw Marketo webhook payload. Find the lead ID, email, first name, company name, job title, company size, industry, country, event type (email_open/click/form_fill/web_visit/download), event URL, event timestamp, current demographic score, and current behavioral score regardless of field naming. Return JSON: {lead_id, email, first_name, company_name, job_title, company_size, industry, country, event_type, event_url, event_timestamp, demo_score, behav_score}",
        "Raw webhook payload: {webhook_payload}",
        [
          { field: "lead_id", as: "mkto_lead_id", type: "string" },
          { field: "email", as: "lead_email", type: "string" },
          { field: "first_name", as: "lead_first_name", type: "string" },
          { field: "company_name", as: "lead_company", type: "string" },
          { field: "job_title", as: "lead_title", type: "string" },
          { field: "company_size", as: "company_size", type: "string" },
          { field: "industry", as: "lead_industry", type: "string" },
          { field: "event_type", as: "event_type", type: "string" },
          { field: "event_url", as: "event_url", type: "string" },
          { field: "demo_score", as: "current_demo_score", type: "string" },
          { field: "behav_score", as: "current_behav_score", type: "string" },
        ],
      ),

      // 3. Demographic score calculator runCode
      n("3", "runCode", "Calculate demographic fit score", 520, {
        runCodeDetails: {
          code: `let demoScore = parseInt(variables.current_demo_score) || 0;
const title = (variables.lead_title || '').toLowerCase();
const size = parseInt(variables.company_size) || 0;
const industry = (variables.lead_industry || '').toLowerCase();
// Title scoring
if (title.match(/^(ceo|cto|cfo|coo|vp|svp|evp|director)/)) demoScore += 20;
else if (title.match(/(manager|head|lead)/)) demoScore += 10;
else if (title.match(/(analyst|specialist|coordinator)/)) demoScore += 5;
// Company size scoring
if (size >= 1000) demoScore += 20;
else if (size >= 200) demoScore += 15;
else if (size >= 50) demoScore += 10;
else demoScore += 5;
// Industry scoring
const highValueIndustries = ['technology','financial services','healthcare','saas','software'];
if (highValueIndustries.some(i => industry.includes(i))) demoScore += 15;
else demoScore += 5;
output.demographic_score = Math.min(100, demoScore);
output.demo_grade = demoScore >= 40 ? 'A' : demoScore >= 25 ? 'B' : demoScore >= 15 ? 'C' : 'D';`,
          outputVariables: [
            { name: "demographic_score", type: "number" },
            { name: "demo_grade", type: "string" },
          ],
        },
      }),

      // 4. Behavioral score calculator runCode
      n("4", "runCode", "Calculate behavioral engagement score", 760, {
        runCodeDetails: {
          code: `let behavScore = parseInt(variables.current_behav_score) || 0;
const eventType = (variables.event_type || '').toLowerCase();
const url = (variables.event_url || '').toLowerCase();
// Event type scoring
const eventScores = { 'form_fill': 25, 'download': 20, 'click': 10, 'email_open': 5, 'web_visit': 3 };
behavScore += eventScores[eventType] || 3;
// High-intent page bonus
if (url.includes('pricing')) behavScore += 15;
else if (url.includes('demo')) behavScore += 20;
else if (url.includes('case-study')) behavScore += 10;
else if (url.includes('contact')) behavScore += 15;
else if (url.includes('comparison')) behavScore += 10;
output.behavioral_score = Math.min(100, behavScore);
output.behav_grade = behavScore >= 40 ? 'A' : behavScore >= 25 ? 'B' : behavScore >= 15 ? 'C' : 'D';
output.event_points_added = eventScores[eventType] || 3;`,
          outputVariables: [
            { name: "behavioral_score", type: "number" },
            { name: "behav_grade", type: "string" },
            { name: "event_points_added", type: "number" },
          ],
        },
      }),

      // 5. Score aggregator with decay runCode
      n("5", "runCode", "Aggregate scores and apply decay logic", 1000, {
        runCodeDetails: {
          code: `const demo = parseInt(variables.demographic_score) || 0;
const behav = parseInt(variables.behavioral_score) || 0;
const totalScore = demo + behav;
const mqlThreshold = 80;
const isMql = totalScore >= mqlThreshold ? 'yes' : 'no';
const combined_grade = variables.demo_grade + variables.behav_grade;
const distance_to_mql = Math.max(0, mqlThreshold - totalScore);
output.total_lead_score = totalScore;
output.is_mql = isMql;
output.combined_grade = combined_grade;
output.distance_to_mql = distance_to_mql;
output.score_summary = 'Demo: ' + demo + ' (' + variables.demo_grade + ') + Behav: ' + behav + ' (' + variables.behav_grade + ') = ' + totalScore + '. MQL: ' + isMql;`,
          outputVariables: [
            { name: "total_lead_score", type: "number" },
            { name: "is_mql", type: "string" },
            { name: "combined_grade", type: "string" },
            { name: "distance_to_mql", type: "number" },
            { name: "score_summary", type: "string" },
          ],
        },
      }),

      // 6. MQL threshold checker condition
      n("6", "condition", "Check if lead has reached MQL threshold", 1240, {
        conditionDetails: {
          conditions: [
            { variable: "is_mql", operator: "equals", value: "yes" },
          ],
          logicalOperator: "AND",
        },
      }),

      // 7. Marketo score writer API (both paths converge here, but MQL path gets status update too)
      n("7", "hitApi", "Write updated score to Marketo lead record", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://{marketo_instance}.mktorest.com/rest/v1/leads.json",
          headers: [
            { key: "Authorization", value: "Bearer {marketo_access_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "action", value: "updateOnly" },
            {
              key: "input",
              value:
                '[{"id":"{mkto_lead_id}","leadScore":"{total_lead_score}","leadStatus":"MQL_REACHED","demographicScore":"{demographic_score}","behavioralScore":"{behavioral_score}"}]',
            },
          ],
          variables: [
            {
              name: "result[0].status",
              customName: "mkto_score_update_status",
              type: "string",
            },
          ],
        },
      }),

      // 8. Outcome logger Sheets
      sheetsNode(
        "8",
        "Log scoring event and MQL conversion to Sheets",
        1720,
        "LeadScoring",
        `[["${new Date().toISOString()}", "{mkto_lead_id}", "{lead_email}", "{lead_company}", "{event_type}", "{event_points_added}", "{demographic_score}", "{demo_grade}", "{behavioral_score}", "{behav_grade}", "{total_lead_score}", "{is_mql}", "{combined_grade}"]]`,
      ),

      // 6b. Score update only (False — not yet MQL)
      n(
        "9",
        "hitApi",
        "Write updated score to Marketo without MQL flag",
        1480,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://{marketo_instance}.mktorest.com/rest/v1/leads.json",
            headers: [
              { key: "Authorization", value: "Bearer {marketo_access_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              { key: "action", value: "updateOnly" },
              {
                key: "input",
                value:
                  '[{"id":"{mkto_lead_id}","leadScore":"{total_lead_score}","demographicScore":"{demographic_score}","behavioralScore":"{behavioral_score}"}]',
              },
            ],
            variables: [
              {
                name: "result[0].status",
                customName: "mkto_partial_update",
                type: "string",
              },
            ],
          },
        },
        400,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("6", "9", "output_2"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1557. MARKETO SALES ALERT NOTIFICATION (7 nodes)
  // ===================================================================

  "Marketo Sales Alert Notification": {
    summary:
      "Receives high-intent action webhooks from Marketo, AI-extracts action data, loads lead and account context from HubSpot, classifies urgency with runCode, sends a real-time Slack alert, logs the event to HubSpot CRM, and tracks response metrics in Sheets.",
    nodes: [
      trigger("Webhook: High-intent action from Marketo", "webhook_app", {
        source: "marketo",
        event: "lead.high_intent_action",
        howItStarts:
          "Fires when Marketo detects a high-intent action such as pricing page visit, demo request form fill, competitor comparison page visit, or repeated product page visits, sending the raw action payload",
        setup:
          "Configure Marketo smart campaign triggers for high-intent pages and forms. Set up Slack webhook for sales alerts channel. Configure HubSpot for CRM event logging.",
        inputFields: [],
      }),

      // 2. AI extract action data
      aiNode(
        "2",
        "AI extract high-intent action details from webhook",
        280,
        "Extract high-intent action data from this raw Marketo webhook payload. Find the lead ID, email, first name, last name, company name, job title, action type, action URL, action timestamp, lead score, and number of times this page was visited regardless of field naming. Return JSON: {lead_id, email, first_name, last_name, company_name, job_title, action_type, action_url, action_timestamp, lead_score, visit_count}",
        "Raw webhook payload: {webhook_payload}",
        [
          { field: "lead_id", as: "mkto_lead_id", type: "string" },
          { field: "email", as: "lead_email", type: "string" },
          { field: "first_name", as: "lead_first_name", type: "string" },
          { field: "last_name", as: "lead_last_name", type: "string" },
          { field: "company_name", as: "lead_company", type: "string" },
          { field: "job_title", as: "lead_title", type: "string" },
          { field: "action_type", as: "intent_action", type: "string" },
          { field: "action_url", as: "intent_url", type: "string" },
          { field: "lead_score", as: "lead_score", type: "string" },
          { field: "visit_count", as: "visit_count", type: "string" },
        ],
      ),

      // 3. Lead account context loader HubSpot
      hubspotGetNode(
        "3",
        "Load lead and account context from HubSpot",
        520,
        "crm/v3/objects/contacts/{lead_email}?properties=email,firstname,lastname,company,jobtitle,lifecyclestage,hs_lead_status,hubspot_owner_id,num_associated_deals,hs_analytics_num_page_views,notes_last_updated",
        [
          {
            name: "properties.lifecyclestage",
            customName: "hs_lifecycle_stage",
            type: "string",
          },
          {
            name: "properties.hs_lead_status",
            customName: "hs_lead_status",
            type: "string",
          },
          {
            name: "properties.hubspot_owner_id",
            customName: "assigned_owner_id",
            type: "string",
          },
          {
            name: "properties.num_associated_deals",
            customName: "open_deals",
            type: "string",
          },
          {
            name: "properties.hs_analytics_num_page_views",
            customName: "total_page_views",
            type: "string",
          },
        ],
      ),

      // 4. Urgency classifier runCode
      n(
        "4",
        "runCode",
        "Classify alert urgency and recommended response",
        760,
        {
          runCodeDetails: {
            code: `const action = (variables.intent_action || '').toLowerCase();
const url = (variables.intent_url || '').toLowerCase();
const score = parseInt(variables.lead_score) || 0;
const visits = parseInt(variables.visit_count) || 1;
const deals = parseInt(variables.open_deals) || 0;
let urgency = 'medium';
let response_window = '4 hours';
let recommended_action = 'send_email';
if (url.includes('pricing') || url.includes('demo-request') || action.includes('demo')) {
  urgency = 'critical'; response_window = '30 minutes'; recommended_action = 'call_immediately';
} else if (url.includes('comparison') || url.includes('vs-') || visits >= 3) {
  urgency = 'high'; response_window = '1 hour'; recommended_action = 'send_personalized_email';
} else if (score >= 70 || deals > 0) {
  urgency = 'high'; response_window = '2 hours'; recommended_action = 'check_deal_status';
}
output.alert_urgency = urgency;
output.response_window = response_window;
output.recommended_action = recommended_action;
output.context_summary = 'Score: ' + score + ' | Visits: ' + visits + ' | Open Deals: ' + deals + ' | Stage: ' + (variables.hs_lifecycle_stage || 'unknown');`,
            outputVariables: [
              { name: "alert_urgency", type: "string" },
              { name: "response_window", type: "string" },
              { name: "recommended_action", type: "string" },
              { name: "context_summary", type: "string" },
            ],
          },
        },
      ),

      // 5. Real-time Slack alert
      slackNode(
        "5",
        "Send real-time sales alert to Slack",
        1000,
        ":rotating_light: *Sales Alert — {alert_urgency} Urgency*\\n>*Lead:* {lead_first_name} {lead_last_name} ({lead_email})\\n>*Company:* {lead_company} | {lead_title}\\n>*Action:* {intent_action} on {intent_url}\\n>*Score:* {lead_score} | Stage: {hs_lifecycle_stage}\\n>*Context:* {context_summary}\\n>\\n>:clock1: *Respond within {response_window}*\\n>:point_right: *Recommended:* {recommended_action}\\n>*Owner:* <@{assigned_owner_id}>",
      ),

      // 6. CRM event logger HubSpot
      hubspotPostNode(
        "6",
        "Log high-intent event to HubSpot timeline",
        1240,
        "crm/v3/objects/contacts/{lead_email}",
        [
          {
            key: "properties",
            value:
              '{"notes_last_updated":"HIGH-INTENT ALERT: {intent_action} on {intent_url}. Urgency: {alert_urgency}. Response window: {response_window}. Action: {recommended_action}. Score: {lead_score}."}',
          },
        ],
        [{ name: "id", customName: "hs_updated_contact_id", type: "string" }],
      ),

      // 7. Response tracker Sheets
      sheetsNode(
        "7",
        "Log sales alert for response time tracking",
        1480,
        "SalesAlerts",
        `[["${new Date().toISOString()}", "{lead_email}", "{lead_first_name} {lead_last_name}", "{lead_company}", "{intent_action}", "{intent_url}", "{lead_score}", "{alert_urgency}", "{response_window}", "{recommended_action}", "{assigned_owner_id}", ""]]`,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1558. MEETING FOLLOW-UP AUTOMATOR (8 nodes)
  // ===================================================================

  "Meeting Follow-Up Automator": {
    summary:
      "Receives a meeting-ended webhook, AI-extracts meeting metadata, fetches recording/notes from the conferencing API, AI generates a structured summary with action items, creates tasks in Asana, sends an external recap email, updates the CRM deal in HubSpot, and schedules a deadline reminder delay.",
    nodes: [
      trigger("Webhook: Meeting ended notification", "webhook_app", {
        source: "zoom",
        event: "meeting.ended",
        howItStarts:
          "Fires when a Zoom/Teams meeting ends, sending the raw meeting-ended payload with meeting ID, participants, duration, recording links, and transcript availability",
        setup:
          "Configure Zoom webhook for meeting.ended events. Set up Asana API for task creation. Configure email service for external recaps. Ensure HubSpot deal access for CRM updates.",
        inputFields: [],
      }),

      // 2. AI extract meeting data
      aiNode(
        "2",
        "AI extract meeting metadata from webhook",
        280,
        "Extract meeting metadata from this raw webhook payload. Find the meeting ID, meeting topic/title, host email, participant emails (comma-separated), start time, end time, duration in minutes, recording URL, transcript URL, and any meeting notes shared during the session regardless of field naming. Return JSON: {meeting_id, meeting_title, host_email, participant_emails, start_time, end_time, duration_minutes, recording_url, transcript_url, inline_notes}",
        "Raw webhook payload: {webhook_payload}",
        [
          { field: "meeting_id", as: "meeting_id", type: "string" },
          { field: "meeting_title", as: "meeting_title", type: "string" },
          { field: "host_email", as: "host_email", type: "string" },
          {
            field: "participant_emails",
            as: "participant_emails",
            type: "string",
          },
          { field: "duration_minutes", as: "meeting_duration", type: "string" },
          { field: "recording_url", as: "recording_url", type: "string" },
          { field: "transcript_url", as: "transcript_url", type: "string" },
          { field: "inline_notes", as: "raw_notes", type: "string" },
        ],
      ),

      // 3. Notes recording fetcher API
      n("3", "hitApi", "Fetch meeting recording and transcript", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.zoom.us/v2/meetings/{meeting_id}/recordings",
          headers: [
            { key: "Authorization", value: "Bearer {zoom_api_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "recording_files[0].download_url",
              customName: "download_url",
              type: "string",
            },
            {
              name: "recording_files[0].file_type",
              customName: "file_type",
              type: "string",
            },
            {
              name: "participant_count",
              customName: "total_participants",
              type: "string",
            },
          ],
        },
      }),

      // 4. AI summary and next steps generator
      aiNode(
        "4",
        "AI generate meeting summary and action items",
        760,
        "You are a meeting summarizer. Given the meeting metadata and any available notes/transcript, generate a structured summary. Include: executive summary (2-3 sentences), key discussion points (bulleted), decisions made, action items with owners and due dates, open questions, and next meeting suggestion. If no transcript is available, generate a template based on the meeting title and participants. Return JSON: {executive_summary, key_points, decisions, action_items, open_questions, next_meeting_suggestion, follow_up_deadline}",
        "Meeting: {meeting_title} | Duration: {meeting_duration} min | Host: {host_email} | Participants: {participant_emails} | Notes: {raw_notes} | Recording: {recording_url} | Transcript: {transcript_url}",
        [
          { field: "executive_summary", as: "exec_summary", type: "string" },
          { field: "key_points", as: "key_points", type: "string" },
          { field: "decisions", as: "decisions_made", type: "string" },
          { field: "action_items", as: "action_items", type: "string" },
          { field: "open_questions", as: "open_questions", type: "string" },
          {
            field: "next_meeting_suggestion",
            as: "next_meeting",
            type: "string",
          },
          {
            field: "follow_up_deadline",
            as: "follow_up_deadline",
            type: "string",
          },
        ],
      ),

      // 5. Task creator Asana API
      n("5", "hitApi", "Create follow-up tasks in Asana", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://app.asana.com/api/1.0/tasks",
          headers: [
            { key: "Authorization", value: "Bearer {asana_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "data",
              value:
                '{"name":"Follow-up: {meeting_title}","notes":"Action Items:\\n{action_items}\\n\\nDecisions:\\n{decisions_made}\\n\\nOpen Questions:\\n{open_questions}","due_on":"{follow_up_deadline}","projects":["{asana_project_id}"],"assignee":"{host_email}"}',
            },
          ],
          variables: [
            { name: "data.gid", customName: "asana_task_id", type: "string" },
            {
              name: "data.permalink_url",
              customName: "asana_task_url",
              type: "string",
            },
          ],
        },
      }),

      // 6. External recap email
      emailNode(
        "6",
        "Send meeting recap email to all participants",
        1240,
        "{participant_emails}",
        "Recap: {meeting_title}",
        "Hi everyone,\\n\\nThank you for joining today's meeting. Here is a summary:\\n\\n--- Executive Summary ---\\n{exec_summary}\\n\\n--- Key Points ---\\n{key_points}\\n\\n--- Decisions Made ---\\n{decisions_made}\\n\\n--- Action Items ---\\n{action_items}\\n\\n--- Open Questions ---\\n{open_questions}\\n\\n--- Next Steps ---\\n{next_meeting}\\n\\nRecording: {recording_url}\\nTask Board: {asana_task_url}\\n\\nPlease review and flag any corrections by end of day.\\n\\nBest regards",
      ),

      // 7. CRM update HubSpot
      hubspotPostNode(
        "7",
        "Update HubSpot deal with meeting outcomes",
        1480,
        "crm/v3/objects/deals/{meeting_id}",
        [
          {
            key: "properties",
            value:
              '{"notes_last_updated":"Meeting: {meeting_title} ({meeting_duration} min). Summary: {exec_summary}. Actions: {action_items}. Next: {next_meeting}. Asana: {asana_task_url}"}',
          },
        ],
        [{ name: "id", customName: "hs_deal_updated", type: "string" }],
      ),

      // 8. Deadline reminder scheduler delay
      n("8", "delayAction", "Schedule reminder for follow-up deadline", 1720, {
        delayDetails: { duration: 3, unit: "days" },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1559. MEETING NO-SHOW RESCHEDULE (7 nodes)
  // ===================================================================

  "Meeting No-Show Reschedule": {
    summary:
      "Receives a no-show detection webhook, AI-extracts meeting data, loads prospect context from HubSpot, AI writes a tactful reschedule message, sends the reschedule email, marks the no-show in HubSpot CRM, and checks rebooking status via condition.",
    nodes: [
      trigger("Webhook: Meeting no-show detected", "webhook_app", {
        source: "calendly",
        event: "invitee.no_show",
        howItStarts:
          "Fires when a scheduled meeting results in a no-show (host joined but invitee did not appear within the grace period), sending the raw no-show event payload",
        setup:
          "Configure Calendly webhook for invitee.no_show events. Set up HubSpot API for contact lookups and activity logging. Prepare reschedule booking link.",
        inputFields: [],
      }),

      // 2. AI extract meeting data
      aiNode(
        "2",
        "AI extract no-show meeting details from webhook",
        280,
        "Extract meeting no-show data from this raw webhook payload. Find the invitee email, invitee name, meeting type, scheduled start time, scheduled end time, host name, host email, event URI, reschedule URL, cancellation reason if any, and number of previous no-shows regardless of field naming. Return JSON: {invitee_email, invitee_name, meeting_type, scheduled_start, scheduled_end, host_name, host_email, event_uri, reschedule_url, cancel_reason, previous_no_shows}",
        "Raw webhook payload: {webhook_payload}",
        [
          { field: "invitee_email", as: "invitee_email", type: "string" },
          { field: "invitee_name", as: "invitee_name", type: "string" },
          { field: "meeting_type", as: "meeting_type", type: "string" },
          { field: "scheduled_start", as: "scheduled_time", type: "string" },
          { field: "host_name", as: "host_name", type: "string" },
          { field: "host_email", as: "host_email", type: "string" },
          { field: "reschedule_url", as: "reschedule_url", type: "string" },
          { field: "previous_no_shows", as: "no_show_count", type: "string" },
        ],
      ),

      // 3. Prospect context loader HubSpot
      hubspotGetNode(
        "3",
        "Load prospect context and engagement history",
        520,
        "crm/v3/objects/contacts/{invitee_email}?properties=email,firstname,lastname,company,jobtitle,lifecyclestage,hs_lead_status,num_associated_deals,hs_email_last_open_date,notes_last_updated",
        [
          {
            name: "properties.company",
            customName: "prospect_company",
            type: "string",
          },
          {
            name: "properties.jobtitle",
            customName: "prospect_title",
            type: "string",
          },
          {
            name: "properties.lifecyclestage",
            customName: "prospect_stage",
            type: "string",
          },
          {
            name: "properties.num_associated_deals",
            customName: "prospect_deals",
            type: "string",
          },
          {
            name: "properties.hs_email_last_open_date",
            customName: "last_email_open",
            type: "string",
          },
        ],
      ),

      // 4. AI reschedule message writer
      aiNode(
        "4",
        "AI craft tactful reschedule message",
        760,
        "You are a sales communication expert. Write a tactful, empathetic reschedule email for a prospect who missed a meeting. Do NOT guilt-trip. Acknowledge that things come up, keep it brief, make it easy to rebook. Adjust tone based on prospect stage (early = lighter, late = more invested). If this is a repeat no-show, be slightly more direct while staying professional. Return JSON: {subject_line, email_body, tone_used, internal_note}",
        "Invitee: {invitee_name} ({invitee_email}) | Company: {prospect_company} | Title: {prospect_title} | Meeting Type: {meeting_type} | Scheduled: {scheduled_time} | Host: {host_name} | Stage: {prospect_stage} | Deals: {prospect_deals} | No-show count: {no_show_count} | Reschedule link: {reschedule_url}",
        [
          { field: "subject_line", as: "reschedule_subject", type: "string" },
          { field: "email_body", as: "reschedule_body", type: "string" },
          { field: "tone_used", as: "tone_used", type: "string" },
          { field: "internal_note", as: "internal_note", type: "string" },
        ],
      ),

      // 5. Reschedule email
      emailNode(
        "5",
        "Send tactful reschedule email to no-show prospect",
        1000,
        "{invitee_email}",
        "{reschedule_subject}",
        "{reschedule_body}",
      ),

      // 6. CRM no-show marker HubSpot
      hubspotPostNode(
        "6",
        "Mark no-show event in HubSpot CRM",
        1240,
        "crm/v3/objects/contacts/{invitee_email}",
        [
          {
            key: "properties",
            value:
              '{"notes_last_updated":"NO-SHOW: {meeting_type} scheduled for {scheduled_time}. Host: {host_name}. Reschedule email sent (tone: {tone_used}). No-show count: {no_show_count}. Note: {internal_note}"}',
          },
        ],
        [{ name: "id", customName: "hs_noshow_contact_id", type: "string" }],
      ),

      // 7. Rebooking check condition
      n("7", "condition", "Check if prospect has multiple no-shows", 1480, {
        conditionDetails: {
          conditions: [
            { variable: "no_show_count", operator: "greater_than", value: "2" },
          ],
          logicalOperator: "AND",
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1560. MEETING NOTES TO CRM LOGGER (7 nodes)
  // ===================================================================

  "Meeting Notes to CRM Logger": {
    summary:
      "Receives a webhook when meeting notes are finalized, AI-extracts structured notes, AI converts to CRM-friendly fields, matches to HubSpot contact and deal, writes the activity log, updates deal fields, and alerts the internal team via Slack.",
    nodes: [
      trigger("Webhook: Meeting notes finalized", "webhook_app", {
        source: "notion",
        event: "page.updated",
        howItStarts:
          "Fires when meeting notes are marked as finalized in Notion (or similar note-taking tool), sending the raw page update payload with structured notes content",
        setup:
          "Configure Notion webhook for page updates with 'Meeting Notes' database filter. Set up HubSpot API for contact/deal matching and activity logging. Configure Slack webhook for team notifications.",
        inputFields: [],
      }),

      // 2. AI extract structured notes
      aiNode(
        "2",
        "AI extract structured meeting notes from webhook",
        280,
        "Extract structured meeting notes from this raw webhook payload. Find the meeting title, meeting date, attendee names, attendee emails, meeting summary, key discussion topics, action items with owners, decisions made, next steps, deal or opportunity referenced, and any budget or timeline information mentioned regardless of field naming. Return JSON: {meeting_title, meeting_date, attendee_names, attendee_emails, summary, topics, action_items, decisions, next_steps, deal_reference, budget_info, timeline_info}",
        "Raw webhook payload: {webhook_payload}",
        [
          { field: "meeting_title", as: "notes_title", type: "string" },
          { field: "meeting_date", as: "notes_date", type: "string" },
          { field: "attendee_emails", as: "attendee_emails", type: "string" },
          { field: "summary", as: "notes_summary", type: "string" },
          { field: "action_items", as: "notes_actions", type: "string" },
          { field: "decisions", as: "notes_decisions", type: "string" },
          { field: "next_steps", as: "notes_next_steps", type: "string" },
          { field: "deal_reference", as: "deal_reference", type: "string" },
          { field: "budget_info", as: "budget_info", type: "string" },
          { field: "timeline_info", as: "timeline_info", type: "string" },
        ],
      ),

      // 3. AI CRM field converter
      aiNode(
        "3",
        "AI convert notes to CRM-friendly field values",
        520,
        "You are a CRM data specialist. Convert these meeting notes into structured CRM field values. Map the summary to a deal note, extract BANT qualification details (Budget, Authority, Need, Timeline), identify the primary contact, determine deal stage recommendation, and flag any risk indicators. Return JSON: {crm_note, bant_budget, bant_authority, bant_need, bant_timeline, primary_contact_email, stage_recommendation, risk_flags, engagement_quality}",
        "Title: {notes_title} | Date: {notes_date} | Summary: {notes_summary} | Decisions: {notes_decisions} | Actions: {notes_actions} | Next Steps: {notes_next_steps} | Budget: {budget_info} | Timeline: {timeline_info} | Deal: {deal_reference}",
        [
          { field: "crm_note", as: "crm_formatted_note", type: "string" },
          {
            field: "primary_contact_email",
            as: "primary_contact",
            type: "string",
          },
          {
            field: "stage_recommendation",
            as: "stage_recommendation",
            type: "string",
          },
          { field: "risk_flags", as: "risk_flags", type: "string" },
          {
            field: "engagement_quality",
            as: "engagement_quality",
            type: "string",
          },
          { field: "bant_budget", as: "bant_budget", type: "string" },
          { field: "bant_timeline", as: "bant_timeline", type: "string" },
        ],
      ),

      // 4. Contact deal matcher HubSpot
      hubspotGetNode(
        "4",
        "Match contact and deal in HubSpot",
        760,
        "crm/v3/objects/contacts/{primary_contact}?properties=email,firstname,lastname,company,num_associated_deals,associatedcompanyid&associations=deals",
        [
          { name: "id", customName: "matched_contact_id", type: "string" },
          {
            name: "properties.company",
            customName: "matched_company",
            type: "string",
          },
          {
            name: "properties.num_associated_deals",
            customName: "contact_deal_count",
            type: "string",
          },
          {
            name: "associations.deals.results[0].id",
            customName: "matched_deal_id",
            type: "string",
          },
        ],
      ),

      // 5. Activity writer HubSpot
      hubspotPostNode(
        "5",
        "Write meeting activity log to HubSpot contact",
        1000,
        "crm/v3/objects/contacts/{matched_contact_id}",
        [
          {
            key: "properties",
            value: '{"notes_last_updated":"{crm_formatted_note}"}',
          },
        ],
        [{ name: "id", customName: "activity_logged_contact", type: "string" }],
      ),

      // 6. Deal field updater HubSpot
      hubspotPostNode(
        "6",
        "Update deal fields with meeting insights",
        1240,
        "crm/v3/objects/deals/{matched_deal_id}",
        [
          {
            key: "properties",
            value:
              '{"notes_last_updated":"Meeting: {notes_title} ({notes_date}). {crm_formatted_note} | Budget: {bant_budget} | Timeline: {bant_timeline} | Risk: {risk_flags} | Quality: {engagement_quality} | Stage Rec: {stage_recommendation}"}',
          },
        ],
        [
          { name: "id", customName: "updated_deal_id", type: "string" },
          {
            name: "properties.dealstage",
            customName: "current_deal_stage",
            type: "string",
          },
        ],
      ),

      // 7. Internal team alert Slack
      slackNode(
        "7",
        "Notify internal team of meeting notes logged",
        1480,
        ":memo: *Meeting Notes Logged to CRM*\\n>*Meeting:* {notes_title} ({notes_date})\\n>*Contact:* {primary_contact} at {matched_company}\\n>*Deal:* {matched_deal_id} | Stage: {current_deal_stage}\\n>*Summary:* {notes_summary}\\n>*Actions:* {notes_actions}\\n>*Next Steps:* {notes_next_steps}\\n>*BANT:* Budget: {bant_budget} | Timeline: {bant_timeline}\\n>*Quality:* {engagement_quality} | *Risk:* {risk_flags}\\n>*Stage Recommendation:* {stage_recommendation}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1561. MEETING-BOOKED REMINDER SEQUENCE (7 nodes)
  // ===================================================================

  "Meeting-Booked Reminder Sequence": {
    summary:
      "Receives a booking-confirmed webhook, AI-extracts booking details, sends an immediate confirmation email, waits 2 days and sends a prep materials email, waits 1 more day and sends a day-of reminder email plus SMS.",
    nodes: [
      trigger("Webhook: Meeting booking confirmed", "webhook_app", {
        source: "calendly",
        event: "invitee.created",
        howItStarts:
          "Fires when a new meeting is booked via Calendly (or similar scheduling tool), sending the raw booking confirmation payload with invitee details, event type, and scheduled time",
        setup:
          "Configure Calendly webhook for invitee.created events. Prepare email templates for confirmation, prep materials, and day-of reminder. Set up SMS provider for mobile reminders.",
        inputFields: [],
      }),

      // 2. AI extract booking data
      aiNode(
        "2",
        "AI extract meeting booking details from webhook",
        280,
        "Extract meeting booking data from this raw webhook payload. Find the invitee email, invitee name, invitee phone number, event type name, scheduled start datetime, scheduled end datetime, host name, host email, meeting location or URL, timezone, any pre-meeting questions answered, and cancellation/reschedule links regardless of field naming. Return JSON: {invitee_email, invitee_name, invitee_phone, event_type, start_datetime, end_datetime, host_name, host_email, meeting_url, timezone, pre_answers, cancel_url, reschedule_url}",
        "Raw webhook payload: {webhook_payload}",
        [
          { field: "invitee_email", as: "invitee_email", type: "string" },
          { field: "invitee_name", as: "invitee_name", type: "string" },
          { field: "invitee_phone", as: "invitee_phone", type: "string" },
          { field: "event_type", as: "event_type", type: "string" },
          { field: "start_datetime", as: "meeting_start", type: "string" },
          { field: "host_name", as: "host_name", type: "string" },
          { field: "host_email", as: "host_email", type: "string" },
          { field: "meeting_url", as: "meeting_url", type: "string" },
          { field: "timezone", as: "meeting_tz", type: "string" },
          { field: "reschedule_url", as: "reschedule_url", type: "string" },
        ],
      ),

      // 3. Confirmation email
      emailNode(
        "3",
        "Send instant booking confirmation email",
        520,
        "{invitee_email}",
        "Confirmed: {event_type} with {host_name}",
        "Hi {invitee_name},\\n\\nYour meeting is confirmed! Here are the details:\\n\\nMeeting: {event_type}\\nDate/Time: {meeting_start} ({meeting_tz})\\nHost: {host_name}\\nJoin Link: {meeting_url}\\n\\nNeed to reschedule? Use this link: {reschedule_url}\\n\\nWe look forward to speaking with you!\\n\\nBest regards,\\n{host_name}",
      ),

      // 4. Delay 2 days
      n("4", "delayAction", "Wait 2 days before sending prep materials", 760, {
        delayDetails: { duration: 2, unit: "days" },
      }),

      // 5. Prep materials email
      emailNode(
        "5",
        "Send pre-meeting preparation materials",
        1000,
        "{invitee_email}",
        "Prepare for your {event_type} with {host_name}",
        "Hi {invitee_name},\\n\\nYour {event_type} with {host_name} is coming up soon ({meeting_start}).\\n\\nTo make the most of our time together, here are a few things to prepare:\\n\\n1. Think about your top 2-3 priorities or challenges you would like to discuss\\n2. Have any relevant data or metrics handy\\n3. Review our quick-start guide: [Resource Link]\\n\\nMeeting Link: {meeting_url}\\n\\nSee you soon!\\n\\nBest,\\n{host_name}",
      ),

      // 6. Delay 1 day
      n("6", "delayAction", "Wait 1 day for day-of reminder", 1240, {
        delayDetails: { duration: 1, unit: "days" },
      }),

      // 7. Day-of reminder email + SMS
      emailNode(
        "7",
        "Send day-of meeting reminder email",
        1480,
        "{invitee_email}",
        "Reminder: {event_type} today with {host_name}",
        "Hi {invitee_name},\\n\\nJust a friendly reminder that your {event_type} with {host_name} is today at {meeting_start} ({meeting_tz}).\\n\\nJoin here: {meeting_url}\\n\\nIf something came up and you need to reschedule: {reschedule_url}\\n\\nSee you soon!",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1562. MONDAY.COM TO HUBSPOT DEAL SYNC (8 nodes)
  // ===================================================================

  "Monday.com to HubSpot Deal Sync": {
    summary:
      "Receives Monday.com item change webhooks, AI-extracts item data, maps fields with runCode, looks up the existing HubSpot deal, creates or updates the deal, applies loop prevention logic, handles conflicts via condition, and logs the sync to Sheets.",
    nodes: [
      trigger("Webhook: Monday.com item changed", "webhook_app", {
        source: "monday",
        event: "item.updated",
        howItStarts:
          "Fires when an item is created or updated in a Monday.com board, sending the raw change event payload with item ID, column values, board ID, and change details",
        setup:
          "Configure Monday.com webhook integration for item change events on the deals board. Set up HubSpot API token with deal read/write access. Prepare Google Sheets for sync audit logging.",
        inputFields: [],
      }),

      // 2. AI extract item data
      aiNode(
        "2",
        "AI extract Monday.com item data from webhook",
        280,
        "Extract Monday.com item data from this raw webhook payload. Find the item ID, item name, board ID, group name, column values (status, person, date, numbers, text), last updated timestamp, pulse ID, creator ID, and which specific columns changed regardless of Monday.com payload structure. Return JSON: {item_id, item_name, board_id, group_name, status_value, person_assigned, deal_value, close_date, company_name, contact_email, changed_columns, updated_at}",
        "Raw webhook payload: {webhook_payload}",
        [
          { field: "item_id", as: "monday_item_id", type: "string" },
          { field: "item_name", as: "monday_item_name", type: "string" },
          { field: "board_id", as: "monday_board_id", type: "string" },
          { field: "status_value", as: "monday_status", type: "string" },
          { field: "deal_value", as: "monday_deal_value", type: "string" },
          { field: "close_date", as: "monday_close_date", type: "string" },
          { field: "company_name", as: "monday_company", type: "string" },
          {
            field: "contact_email",
            as: "monday_contact_email",
            type: "string",
          },
          { field: "changed_columns", as: "changed_columns", type: "string" },
          { field: "updated_at", as: "monday_updated_at", type: "string" },
        ],
      ),

      // 3. Field mapper runCode
      n(
        "3",
        "runCode",
        "Map Monday.com fields to HubSpot deal properties",
        520,
        {
          runCodeDetails: {
            code: `const statusMap = {
  'Working on it': 'appointmentscheduled',
  'Qualified': 'qualifiedtobuy',
  'Proposal Sent': 'presentationscheduled',
  'Negotiating': 'decisionmakerboughtin',
  'Won': 'closedwon',
  'Lost': 'closedlost',
  'Stuck': 'appointmentscheduled'
};
const mondayStatus = variables.monday_status || '';
const hsStage = statusMap[mondayStatus] || 'appointmentscheduled';
const amount = variables.monday_deal_value || '0';
const closeDate = variables.monday_close_date || '';
output.hs_deal_stage = hsStage;
output.hs_deal_amount = amount;
output.hs_close_date = closeDate;
output.hs_deal_name = variables.monday_item_name || 'Untitled Deal';
output.sync_fingerprint = variables.monday_item_id + '_' + variables.monday_updated_at;
output.field_mapping_summary = 'Monday status [' + mondayStatus + '] → HubSpot stage [' + hsStage + '] | Amount: $' + amount;`,
            outputVariables: [
              { name: "hs_deal_stage", type: "string" },
              { name: "hs_deal_amount", type: "string" },
              { name: "hs_close_date", type: "string" },
              { name: "hs_deal_name", type: "string" },
              { name: "sync_fingerprint", type: "string" },
              { name: "field_mapping_summary", type: "string" },
            ],
          },
        },
      ),

      // 4. HubSpot deal lookup API
      hubspotGetNode(
        "4",
        "Look up existing HubSpot deal by Monday ID",
        760,
        "crm/v3/objects/deals/search",
        [
          { name: "total", customName: "hs_deal_match_count", type: "number" },
          {
            name: "results[0].id",
            customName: "existing_hs_deal_id",
            type: "string",
          },
          {
            name: "results[0].properties.dealstage",
            customName: "existing_hs_stage",
            type: "string",
          },
          {
            name: "results[0].properties.hs_lastmodifieddate",
            customName: "hs_last_modified",
            type: "string",
          },
        ],
      ),

      // 5. Create or update HubSpot deal
      hubspotPostNode(
        "5",
        "Create or update HubSpot deal from Monday data",
        1000,
        "crm/v3/objects/deals",
        [
          {
            key: "properties",
            value:
              '{"dealname":"{hs_deal_name}","dealstage":"{hs_deal_stage}","amount":"{hs_deal_amount}","closedate":"{hs_close_date}","description":"Synced from Monday.com (Item: {monday_item_id}, Board: {monday_board_id}). {field_mapping_summary}","monday_item_id":"{monday_item_id}","sync_fingerprint":"{sync_fingerprint}"}',
          },
        ],
        [
          { name: "id", customName: "synced_deal_id", type: "string" },
          {
            name: "properties.dealstage",
            customName: "synced_deal_stage",
            type: "string",
          },
        ],
      ),

      // 6. Loop prevention runCode
      n("6", "runCode", "Apply loop prevention and detect conflicts", 1240, {
        runCodeDetails: {
          code: `const existingId = variables.existing_hs_deal_id || '';
const matchCount = parseInt(variables.hs_deal_match_count) || 0;
const existingStage = variables.existing_hs_stage || '';
const newStage = variables.hs_deal_stage;
const hsModified = new Date(variables.hs_last_modified || 0).getTime();
const mondayModified = new Date(variables.monday_updated_at || 0).getTime();
let has_conflict = 'no';
let conflict_type = 'none';
if (matchCount > 0 && existingStage !== newStage) {
  if (hsModified > mondayModified) {
    has_conflict = 'yes';
    conflict_type = 'hubspot_newer';
  }
}
output.has_conflict = has_conflict;
output.conflict_type = conflict_type;
output.sync_type = matchCount > 0 ? 'update' : 'create';
output.sync_result = 'Monday [' + variables.monday_item_id + '] → HubSpot [' + (variables.synced_deal_id || existingId) + '] (' + (matchCount > 0 ? 'update' : 'create') + ')';`,
          outputVariables: [
            { name: "has_conflict", type: "string" },
            { name: "conflict_type", type: "string" },
            { name: "sync_type", type: "string" },
            { name: "sync_result", type: "string" },
          ],
        },
      }),

      // 7. Conflict handler condition
      n("7", "condition", "Check for sync conflicts", 1480, {
        conditionDetails: {
          conditions: [
            { variable: "has_conflict", operator: "equals", value: "no" },
          ],
          logicalOperator: "AND",
        },
      }),

      // 8a. Sync audit logger Sheets (True — no conflict)
      sheetsNode(
        "8",
        "Log successful sync to audit sheet",
        1720,
        "MondayHubSpotSync",
        `[["${new Date().toISOString()}", "{monday_item_id}", "{monday_item_name}", "{synced_deal_id}", "{sync_type}", "{field_mapping_summary}", "{sync_fingerprint}", "success", "no_conflict"]]`,
      ),

      // 8b. Conflict audit logger Sheets (False — conflict detected)
      sheetsNode(
        "9",
        "Log sync conflict to audit sheet for review",
        1720,
        "MondayHubSpotSync",
        `[["${new Date().toISOString()}", "{monday_item_id}", "{monday_item_name}", "{existing_hs_deal_id}", "{sync_type}", "{field_mapping_summary}", "{sync_fingerprint}", "conflict", "{conflict_type}"]]`,
        400,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
      e("7", "9", "output_2"),
    ],
  },

  // ===================================================================
  // 1563. MULTI CRM SYNC BRIDGE (9 nodes)
  // ===================================================================

  "Multi CRM Sync Bridge": {
    summary:
      "Runs on a schedule to fetch changes from primary CRM (HubSpot) and secondary CRM (API), resolves conflicts with runCode, syncs changes in both directions, logs unresolved conflicts to Sheets, updates a health dashboard in Sheets, and alerts on sync failures via Slack.",
    nodes: [
      trigger(
        "Schedule: Bi-directional CRM sync every 30 minutes",
        "schedule",
        {
          schedule: "Every 30 minutes",
          howItStarts:
            "Runs every 30 minutes to synchronize contact and deal data between HubSpot (primary CRM) and a secondary CRM, detecting changes in both systems and resolving conflicts",
          setup:
            "Configure HubSpot API token with full CRM access. Set up secondary CRM API credentials. Prepare conflict resolution rules. Create Google Sheets for sync audit and health dashboard.",
          inputFields: [],
        },
      ),

      // 2. Primary CRM changes fetch HubSpot
      hubspotGetNode(
        "2",
        "Fetch recent changes from HubSpot primary CRM",
        280,
        "crm/v3/objects/contacts/search",
        [
          {
            name: "results",
            customName: "hs_changed_contacts",
            type: "string",
          },
          { name: "total", customName: "hs_change_count", type: "number" },
          {
            name: "results[0].id",
            customName: "hs_first_contact_id",
            type: "string",
          },
          {
            name: "results[0].properties.email",
            customName: "hs_first_email",
            type: "string",
          },
          {
            name: "results[0].properties.hs_lastmodifieddate",
            customName: "hs_latest_modified",
            type: "string",
          },
        ],
      ),

      // 3. Secondary CRM changes fetch API
      n("3", "hitApi", "Fetch recent changes from secondary CRM", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://{secondary_crm_domain}/api/v2/contacts?modified_after={last_sync_timestamp}&limit=100",
          headers: [
            { key: "Authorization", value: "Bearer {secondary_crm_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "data",
              customName: "secondary_changed_contacts",
              type: "string",
            },
            {
              name: "meta.total",
              customName: "secondary_change_count",
              type: "number",
            },
            { name: "data[0].id", customName: "sec_first_id", type: "string" },
            {
              name: "data[0].email",
              customName: "sec_first_email",
              type: "string",
            },
            {
              name: "data[0].updated_at",
              customName: "sec_latest_modified",
              type: "string",
            },
          ],
        },
      }),

      // 4. Conflict resolver runCode
      n("4", "runCode", "Resolve sync conflicts between CRMs", 760, {
        runCodeDetails: {
          code: `const hsCount = parseInt(variables.hs_change_count) || 0;
const secCount = parseInt(variables.secondary_change_count) || 0;
const hsLatest = new Date(variables.hs_latest_modified || 0).getTime();
const secLatest = new Date(variables.sec_latest_modified || 0).getTime();
let conflicts = [];
let hs_to_sec_count = 0;
let sec_to_hs_count = 0;
let unresolved_count = 0;
if (hsCount > 0 && secCount > 0) {
  // Both systems have changes — check for overlap
  if (variables.hs_first_email === variables.sec_first_email) {
    if (hsLatest > secLatest) {
      hs_to_sec_count++;
    } else if (secLatest > hsLatest) {
      sec_to_hs_count++;
    } else {
      unresolved_count++;
      conflicts.push(variables.hs_first_email);
    }
  } else {
    hs_to_sec_count = hsCount;
    sec_to_hs_count = secCount;
  }
} else {
  hs_to_sec_count = hsCount;
  sec_to_hs_count = secCount;
}
output.hs_to_secondary_count = hs_to_sec_count;
output.secondary_to_hs_count = sec_to_hs_count;
output.unresolved_conflicts = unresolved_count;
output.conflict_emails = conflicts.join(', ') || 'none';
output.sync_summary = 'HS→Sec: ' + hs_to_sec_count + ' | Sec→HS: ' + sec_to_hs_count + ' | Conflicts: ' + unresolved_count;`,
          outputVariables: [
            { name: "hs_to_secondary_count", type: "number" },
            { name: "secondary_to_hs_count", type: "number" },
            { name: "unresolved_conflicts", type: "number" },
            { name: "conflict_emails", type: "string" },
            { name: "sync_summary", type: "string" },
          ],
        },
      }),

      // 5. Primary-to-secondary sync API
      n("5", "hitApi", "Sync HubSpot changes to secondary CRM", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://{secondary_crm_domain}/api/v2/contacts/bulk-upsert",
          headers: [
            { key: "Authorization", value: "Bearer {secondary_crm_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "contacts", value: "{hs_changed_contacts}" },
            { key: "source", value: "hubspot_sync" },
          ],
          variables: [
            { name: "synced", customName: "hs_to_sec_synced", type: "number" },
            { name: "errors", customName: "hs_to_sec_errors", type: "string" },
          ],
        },
      }),

      // 6. Secondary-to-primary sync HubSpot
      hubspotPostNode(
        "6",
        "Sync secondary CRM changes to HubSpot",
        1240,
        "crm/v3/objects/contacts/batch/update",
        [{ key: "inputs", value: "{secondary_changed_contacts}" }],
        [
          { name: "results", customName: "sec_to_hs_results", type: "string" },
          { name: "errors", customName: "sec_to_hs_errors", type: "string" },
        ],
      ),

      // 7. Unresolved conflicts queue Sheets
      sheetsNode(
        "7",
        "Log unresolved conflicts for manual review",
        1480,
        "SyncConflicts",
        `[["${new Date().toISOString()}", "{conflict_emails}", "{unresolved_conflicts}", "{hs_latest_modified}", "{sec_latest_modified}", "pending_review"]]`,
      ),

      // 8. Health dashboard Sheets
      sheetsNode(
        "8",
        "Update sync health dashboard",
        1720,
        "SyncHealth",
        `[["${new Date().toISOString()}", "{hs_change_count}", "{secondary_change_count}", "{hs_to_secondary_count}", "{secondary_to_hs_count}", "{unresolved_conflicts}", "{hs_to_sec_synced}", "{hs_to_sec_errors}", "{sec_to_hs_errors}", "{sync_summary}"]]`,
      ),

      // 9. Sync failure alert Slack
      slackNode(
        "9",
        "Alert on sync issues or conflicts",
        1960,
        ":arrows_counterclockwise: *CRM Sync Bridge Report*\\n>*HubSpot Changes:* {hs_change_count} | *Secondary Changes:* {secondary_change_count}\\n>*HS → Secondary:* {hs_to_sec_synced} synced | Errors: {hs_to_sec_errors}\\n>*Secondary → HS:* {secondary_to_hs_count} | Errors: {sec_to_hs_errors}\\n>*Unresolved Conflicts:* {unresolved_conflicts} ({conflict_emails})\\n>\\n>*Summary:* {sync_summary}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 1564. MULTI-ASSET CONTENT NURTURE BY TOPIC (8 nodes)
  // ===================================================================

  "Multi-Asset Content Nurture by Topic": {
    summary:
      "Receives a topic interest webhook, AI-extracts topic signals, queries the content library with runCode, AI curates a multi-asset content journey, sends a lightweight touch 1 email, waits 5 days, sends a deeper touch 2 email, and evaluates content exhaustion via condition.",
    nodes: [
      trigger("Webhook: Content topic interest detected", "webhook_app", {
        source: "hubspot",
        event: "contact.topic_interest",
        howItStarts:
          "Fires when a contact shows strong interest in a specific content topic through multiple page visits, downloads, or search queries, sending the raw topic interest payload",
        setup:
          "Configure HubSpot webhook for topic interest tracking (page view patterns, content downloads). Maintain a topic-to-content mapping in the content library. Prepare multi-touch email sequences.",
        inputFields: [],
      }),

      // 2. AI extract topic data
      aiNode(
        "2",
        "AI extract topic interest signals from webhook",
        280,
        "Extract content topic interest data from this raw webhook payload. Find the contact email, first name, last name, company name, identified topic, topic confidence score, pages visited related to topic, content downloaded, search queries used, lead score, and total topic-related interactions regardless of field naming. Return JSON: {email, first_name, last_name, company_name, topic, topic_confidence, pages_visited, downloads, search_queries, lead_score, interaction_count}",
        "Raw webhook payload: {webhook_payload}",
        [
          { field: "email", as: "lead_email", type: "string" },
          { field: "first_name", as: "lead_first_name", type: "string" },
          { field: "last_name", as: "lead_last_name", type: "string" },
          { field: "company_name", as: "lead_company", type: "string" },
          { field: "topic", as: "interest_topic", type: "string" },
          { field: "topic_confidence", as: "topic_confidence", type: "string" },
          { field: "downloads", as: "topic_downloads", type: "string" },
          { field: "lead_score", as: "lead_score", type: "string" },
          {
            field: "interaction_count",
            as: "topic_interactions",
            type: "string",
          },
        ],
      ),

      // 3. Content library query runCode
      n("3", "runCode", "Query content library for topic-matched assets", 520, {
        runCodeDetails: {
          code: `const topic = (variables.interest_topic || '').toLowerCase();
const downloads = (variables.topic_downloads || '').toLowerCase();
const contentLibrary = {
  'automation': { light: { title: '5 Ways to Automate Your Marketing Stack', type: 'blog', url: '/blog/marketing-automation-guide' }, deep: { title: 'Marketing Automation ROI Calculator + Case Studies', type: 'toolkit', url: '/resources/automation-roi-toolkit' }, expert: { title: 'Enterprise Automation Playbook', type: 'whitepaper', url: '/resources/enterprise-automation' }},
  'analytics': { light: { title: 'Metrics That Matter: A Leader Guide', type: 'blog', url: '/blog/metrics-that-matter' }, deep: { title: 'Attribution Modeling Deep Dive', type: 'guide', url: '/resources/attribution-modeling' }, expert: { title: 'Building a Data-Driven Culture', type: 'ebook', url: '/resources/data-driven-culture' }},
  'personalization': { light: { title: 'Personalization 101: Quick Wins', type: 'blog', url: '/blog/personalization-quick-wins' }, deep: { title: 'Dynamic Content Strategy Guide', type: 'guide', url: '/resources/dynamic-content-guide' }, expert: { title: 'Hyper-Personalization at Scale', type: 'whitepaper', url: '/resources/hyper-personalization' }},
};
const topicContent = contentLibrary[topic] || contentLibrary['automation'];
const alreadyDownloaded = downloads.includes(topicContent.light.title.toLowerCase());
output.light_title = topicContent.light.title;
output.light_type = topicContent.light.type;
output.light_url = topicContent.light.url;
output.deep_title = topicContent.deep.title;
output.deep_type = topicContent.deep.type;
output.deep_url = topicContent.deep.url;
output.expert_title = topicContent.expert.title;
output.content_exhausted = alreadyDownloaded ? 'yes' : 'no';
output.total_available_assets = '3';`,
          outputVariables: [
            { name: "light_title", type: "string" },
            { name: "light_type", type: "string" },
            { name: "light_url", type: "string" },
            { name: "deep_title", type: "string" },
            { name: "deep_type", type: "string" },
            { name: "deep_url", type: "string" },
            { name: "expert_title", type: "string" },
            { name: "content_exhausted", type: "string" },
            { name: "total_available_assets", type: "string" },
          ],
        },
      }),

      // 4. AI content journey curator
      aiNode(
        "4",
        "AI curate personalized content journey",
        760,
        "You are a content strategist. Given the lead's topic interest, confidence score, and available content assets, curate a two-touch email journey. Touch 1 should be lightweight (blog, infographic, quick tip) to build trust. Touch 2 should be deeper (guide, toolkit, case study) to demonstrate expertise. Personalize the framing based on their company and interaction history. Return JSON: {touch1_hook, touch1_body, touch1_subject, touch2_hook, touch2_body, touch2_subject, content_angle}",
        "Lead: {lead_first_name} {lead_last_name} at {lead_company} | Topic: {interest_topic} (confidence: {topic_confidence}) | Interactions: {topic_interactions} | Score: {lead_score} | Light asset: {light_title} ({light_type}) | Deep asset: {deep_title} ({deep_type}) | Expert: {expert_title}",
        [
          { field: "touch1_hook", as: "t1_hook", type: "string" },
          { field: "touch1_body", as: "t1_body", type: "string" },
          { field: "touch1_subject", as: "t1_subject", type: "string" },
          { field: "touch2_hook", as: "t2_hook", type: "string" },
          { field: "touch2_body", as: "t2_body", type: "string" },
          { field: "touch2_subject", as: "t2_subject", type: "string" },
          { field: "content_angle", as: "content_angle", type: "string" },
        ],
      ),

      // 5. Touch 1 lightweight email
      emailNode(
        "5",
        "Send lightweight touch 1 topic email",
        1000,
        "{lead_email}",
        "{t1_subject}",
        "Hi {lead_first_name},\\n\\n{t1_hook}\\n\\n{t1_body}\\n\\nCheck it out: {light_title} — {light_url}\\n\\nBest regards",
      ),

      // 6. Delay 5 days
      n("6", "delayAction", "Wait 5 days before deeper content touch", 1240, {
        delayDetails: { duration: 5, unit: "days" },
      }),

      // 7. Touch 2 deeper email
      emailNode(
        "7",
        "Send deeper touch 2 content email",
        1480,
        "{lead_email}",
        "{t2_subject}",
        "Hi {lead_first_name},\\n\\n{t2_hook}\\n\\n{t2_body}\\n\\nDive deeper: {deep_title} — {deep_url}\\n\\nIf you are ready for even more, our expert resource on {interest_topic} is available: {expert_title}\\n\\nBest regards",
      ),

      // 8. Exhaustion evaluator condition
      n(
        "8",
        "condition",
        "Check if content for this topic is exhausted",
        1720,
        {
          conditionDetails: {
            conditions: [
              {
                variable: "content_exhausted",
                operator: "equals",
                value: "no",
              },
            ],
            logicalOperator: "AND",
          },
        },
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1565. MULTI-TOUCH NURTURE WITH ENGAGEMENT PROGRAM (9 nodes)
  // ===================================================================

  "Multi-Touch Nurture with Engagement Program": {
    summary:
      "Receives an enrollment webhook, AI-extracts lead data, assigns a nurture stream with runCode, AI selects dynamic content, sends touch 1 email, waits 5 days, branches on engagement, sends touch 2 email for engaged leads, and logs stream performance to Sheets.",
    nodes: [
      trigger("Webhook: Lead enrolled in engagement program", "webhook_app", {
        source: "marketo",
        event: "lead.program_enrolled",
        howItStarts:
          "Fires when a lead is enrolled in a Marketo engagement program (drip nurture), sending the raw enrollment payload with lead details, program info, and stream assignment",
        setup:
          "Configure Marketo webhook for program enrollment events. Define stream-to-content mappings. Set up performance tracking in Google Sheets.",
        inputFields: [],
      }),

      // 2. AI extract lead data
      aiNode(
        "2",
        "AI extract enrollment and lead details from webhook",
        280,
        "Extract engagement program enrollment data from this raw webhook payload. Find the lead email, first name, last name, company name, job title, lead score, program name, stream name, enrollment date, lead source, and any previous program memberships regardless of field naming. Return JSON: {email, first_name, last_name, company_name, job_title, lead_score, program_name, stream_name, enrollment_date, lead_source, previous_programs}",
        "Raw webhook payload: {webhook_payload}",
        [
          { field: "email", as: "lead_email", type: "string" },
          { field: "first_name", as: "lead_first_name", type: "string" },
          { field: "last_name", as: "lead_last_name", type: "string" },
          { field: "company_name", as: "lead_company", type: "string" },
          { field: "job_title", as: "lead_title", type: "string" },
          { field: "lead_score", as: "lead_score", type: "string" },
          { field: "program_name", as: "program_name", type: "string" },
          { field: "stream_name", as: "initial_stream", type: "string" },
          { field: "enrollment_date", as: "enrollment_date", type: "string" },
          { field: "lead_source", as: "lead_source", type: "string" },
        ],
      ),

      // 3. Stream assigner runCode
      n(
        "3",
        "runCode",
        "Assign optimal nurture stream based on lead profile",
        520,
        {
          runCodeDetails: {
            code: `const score = parseInt(variables.lead_score) || 0;
const title = (variables.lead_title || '').toLowerCase();
const source = (variables.lead_source || '').toLowerCase();
let stream, cadence_days, content_depth;
if (score >= 70 || title.match(/^(ceo|cto|vp|director|head)/)) {
  stream = 'fast_track'; cadence_days = 3; content_depth = 'solution_focused';
} else if (score >= 40 || source.includes('webinar') || source.includes('demo')) {
  stream = 'standard'; cadence_days = 5; content_depth = 'educational';
} else {
  stream = 'awareness'; cadence_days = 7; content_depth = 'thought_leadership';
}
output.assigned_stream = stream;
output.cadence_days = cadence_days;
output.content_depth = content_depth;
output.stream_label = stream.replace('_', ' ') + ' (every ' + cadence_days + ' days)';`,
            outputVariables: [
              { name: "assigned_stream", type: "string" },
              { name: "cadence_days", type: "number" },
              { name: "content_depth", type: "string" },
              { name: "stream_label", type: "string" },
            ],
          },
        },
      ),

      // 4. AI dynamic content selector
      aiNode(
        "4",
        "AI select dynamic content for nurture stream",
        760,
        "You are an engagement program manager. Given the lead profile and assigned stream, select the optimal first two content touches. For fast_track: solution-focused case studies and product comparisons. For standard: educational guides and webinar replays. For awareness: thought leadership and industry trends. Return JSON: {touch1_subject, touch1_content_title, touch1_preview, touch1_cta, touch2_subject, touch2_content_title, touch2_preview, touch2_cta, stream_personalization}",
        "Lead: {lead_first_name} {lead_last_name} | Title: {lead_title} | Company: {lead_company} | Score: {lead_score} | Stream: {assigned_stream} | Depth: {content_depth} | Source: {lead_source} | Program: {program_name}",
        [
          { field: "touch1_subject", as: "t1_subject", type: "string" },
          { field: "touch1_content_title", as: "t1_content", type: "string" },
          { field: "touch1_preview", as: "t1_preview", type: "string" },
          { field: "touch1_cta", as: "t1_cta", type: "string" },
          { field: "touch2_subject", as: "t2_subject", type: "string" },
          { field: "touch2_content_title", as: "t2_content", type: "string" },
          { field: "touch2_preview", as: "t2_preview", type: "string" },
          { field: "touch2_cta", as: "t2_cta", type: "string" },
          {
            field: "stream_personalization",
            as: "stream_personalization",
            type: "string",
          },
        ],
      ),

      // 5. Touch 1 email
      emailNode(
        "5",
        "Send engagement program touch 1 email",
        1000,
        "{lead_email}",
        "{t1_subject}",
        "Hi {lead_first_name},\\n\\n{stream_personalization}\\n\\nI wanted to share something I think you will find valuable:\\n\\n{t1_content}\\n{t1_preview}\\n\\n{t1_cta}\\n\\nBest regards",
      ),

      // 6. Delay 5 days
      n("6", "delayAction", "Wait for cadence interval before touch 2", 1240, {
        delayDetails: { duration: 5, unit: "days" },
      }),

      // 7. Engagement measurer condition
      n(
        "7",
        "condition",
        "Check if lead score increased since enrollment",
        1480,
        {
          conditionDetails: {
            conditions: [
              { variable: "lead_score", operator: "greater_than", value: "30" },
            ],
            logicalOperator: "AND",
          },
        },
      ),

      // 8. Touch 2 email (True — engaged)
      emailNode(
        "8",
        "Send engagement program touch 2 email",
        1720,
        "{lead_email}",
        "{t2_subject}",
        "Hi {lead_first_name},\\n\\nI hope you enjoyed {t1_content} — here is the next piece in your personalized journey:\\n\\n{t2_content}\\n{t2_preview}\\n\\n{t2_cta}\\n\\nBest regards",
      ),

      // 9. Stream performance logger Sheets (both paths log)
      sheetsNode(
        "9",
        "Log stream performance metrics to Sheets",
        1960,
        "EngagementProgram",
        `[["${new Date().toISOString()}", "{lead_email}", "{lead_first_name} {lead_last_name}", "{lead_company}", "{program_name}", "{assigned_stream}", "{lead_score}", "{content_depth}", "{t1_content}", "{t2_content}", "{stream_label}"]]`,
      ),

      // 9b. Low engagement stream logger (False — not engaged enough)
      sheetsNode(
        "10",
        "Log low-engagement lead for stream review",
        1720,
        "EngagementProgram",
        `[["${new Date().toISOString()}", "{lead_email}", "{lead_first_name} {lead_last_name}", "{lead_company}", "{program_name}", "{assigned_stream}", "{lead_score}", "low_engagement", "{t1_content}", "skipped_touch2", "{stream_label}"]]`,
        400,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
      e("7", "10", "output_2"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 1566. NEW CONTACT AT EXISTING ACCOUNT WELCOME (7 nodes)
  // ===================================================================

  "New Contact at Existing Account Welcome": {
    summary:
      "Receives a webhook when a new contact is added to an existing account, AI-extracts contact details, loads account and role context from HubSpot, AI builds a role-specific onboarding plan, sends a personalized welcome email, triggers in-app onboarding via API, and alerts the CSM via Slack.",
    nodes: [
      trigger("Webhook: New contact added to existing account", "webhook_app", {
        source: "hubspot",
        event: "contact.created_at_existing_company",
        howItStarts:
          "Fires when a new contact is created in HubSpot that is associated with an existing company/account, sending the raw contact creation payload with contact and company association details",
        setup:
          "Configure HubSpot webhook for contact.creation events filtered to existing company associations. Set up in-app onboarding API. Configure Slack webhook for CSM notifications.",
        inputFields: [],
      }),

      // 2. AI extract contact data
      aiNode(
        "2",
        "AI extract new contact and account details from webhook",
        280,
        "Extract new contact data and existing account association from this raw webhook payload. Find the contact email, first name, last name, job title, phone number, associated company ID, associated company name, how the contact was created (form, import, manual), and any properties set at creation regardless of field naming. Return JSON: {email, first_name, last_name, job_title, phone, company_id, company_name, creation_source, initial_properties}",
        "Raw webhook payload: {webhook_payload}",
        [
          { field: "email", as: "new_contact_email", type: "string" },
          { field: "first_name", as: "new_contact_first", type: "string" },
          { field: "last_name", as: "new_contact_last", type: "string" },
          { field: "job_title", as: "new_contact_title", type: "string" },
          { field: "phone", as: "new_contact_phone", type: "string" },
          { field: "company_id", as: "account_id", type: "string" },
          { field: "company_name", as: "account_name", type: "string" },
          { field: "creation_source", as: "creation_source", type: "string" },
        ],
      ),

      // 3. Account role context loader HubSpot
      hubspotGetNode(
        "3",
        "Load account context and existing contacts",
        520,
        "crm/v3/objects/companies/{account_id}?properties=name,domain,industry,numberofemployees,hs_lead_status,hubspot_owner_id,num_associated_contacts,notes_last_updated&associations=contacts",
        [
          {
            name: "properties.domain",
            customName: "account_domain",
            type: "string",
          },
          {
            name: "properties.industry",
            customName: "account_industry",
            type: "string",
          },
          {
            name: "properties.numberofemployees",
            customName: "account_size",
            type: "string",
          },
          {
            name: "properties.hs_lead_status",
            customName: "account_status",
            type: "string",
          },
          {
            name: "properties.hubspot_owner_id",
            customName: "account_owner_id",
            type: "string",
          },
          {
            name: "properties.num_associated_contacts",
            customName: "total_contacts_at_account",
            type: "string",
          },
        ],
      ),

      // 4. AI role-specific onboarding builder
      aiNode(
        "4",
        "AI build role-specific onboarding plan",
        760,
        "You are a customer success onboarding specialist. Given the new contact's role and their existing account context, create a personalized onboarding plan. Consider: the contact's job title determines what features/modules they need access to and training on. An executive needs dashboards and reporting. A marketer needs campaign tools. A developer needs API docs. An admin needs settings and user management. Return JSON: {welcome_message, onboarding_modules, first_task, helpful_resources, estimated_time_to_value, csm_talking_points}",
        "New Contact: {new_contact_first} {new_contact_last} | Title: {new_contact_title} | Account: {account_name} | Industry: {account_industry} | Size: {account_size} | Status: {account_status} | Total Contacts: {total_contacts_at_account} | Source: {creation_source}",
        [
          { field: "welcome_message", as: "welcome_msg", type: "string" },
          {
            field: "onboarding_modules",
            as: "onboarding_modules",
            type: "string",
          },
          { field: "first_task", as: "first_task", type: "string" },
          { field: "helpful_resources", as: "resources", type: "string" },
          {
            field: "estimated_time_to_value",
            as: "time_to_value",
            type: "string",
          },
          {
            field: "csm_talking_points",
            as: "csm_talking_points",
            type: "string",
          },
        ],
      ),

      // 5. Welcome email
      emailNode(
        "5",
        "Send role-specific welcome email to new contact",
        1000,
        "{new_contact_email}",
        "Welcome to {account_name}'s team on our platform!",
        "Hi {new_contact_first},\\n\\n{welcome_msg}\\n\\nAs a {new_contact_title} at {account_name}, here is your personalized getting-started plan:\\n\\nRecommended Modules: {onboarding_modules}\\n\\nYour First Task: {first_task}\\n\\nHelpful Resources: {resources}\\n\\nEstimated time to see value: {time_to_value}\\n\\nYour Customer Success Manager will reach out shortly to help you get up to speed.\\n\\nWelcome aboard!",
      ),

      // 6. In-app onboarding trigger API
      n("6", "hitApi", "Trigger in-app onboarding flow for new user", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://{app_domain}/api/v1/onboarding/trigger",
          headers: [
            { key: "Authorization", value: "Bearer {app_api_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "user_email", value: "{new_contact_email}" },
            { key: "account_id", value: "{account_id}" },
            { key: "role", value: "{new_contact_title}" },
            { key: "modules", value: "{onboarding_modules}" },
            { key: "first_task", value: "{first_task}" },
          ],
          variables: [
            {
              name: "onboarding_id",
              customName: "onboarding_flow_id",
              type: "string",
            },
            {
              name: "status",
              customName: "onboarding_trigger_status",
              type: "string",
            },
          ],
        },
      }),

      // 7. CSM alert Slack
      slackNode(
        "7",
        "Alert CSM of new contact at existing account",
        1480,
        ":wave: *New Contact at Existing Account*\\n>*Contact:* {new_contact_first} {new_contact_last} ({new_contact_title})\\n>*Email:* {new_contact_email}\\n>*Account:* {account_name} ({account_industry}, {account_size} employees)\\n>*Account Status:* {account_status} | Total Contacts: {total_contacts_at_account}\\n>*Source:* {creation_source}\\n>*Onboarding:* {onboarding_modules}\\n>*Time to Value:* {time_to_value}\\n>\\n>*CSM Talking Points:* {csm_talking_points}\\n>*Owner:* <@{account_owner_id}>",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1567. NEW MQL TO SDR TASK PLUS SLA TIMER (7 nodes)
  // ===================================================================

  "New MQL to SDR Task Plus SLA Timer": {
    summary:
      "Receives an MQL conversion webhook, AI-extracts the MQL snapshot, assigns the right SDR with runCode, creates a follow-up task in Asana, alerts the SDR via Slack, starts an SLA timer delay, and checks SLA breach via condition for escalation.",
    nodes: [
      trigger("Webhook: Lead converted to MQL", "webhook_app", {
        source: "hubspot",
        event: "contact.became_mql",
        howItStarts:
          "Fires when a contact's lifecycle stage changes to MQL in HubSpot, sending the raw MQL conversion event payload with contact details, scoring data, and conversion metadata",
        setup:
          "Configure HubSpot webhook for lifecyclestage change to marketingqualifiedlead. Set up Asana API for task creation. Define SDR assignment rules (round-robin, territory, etc.). Configure SLA thresholds.",
        inputFields: [],
      }),

      // 2. AI extract MQL snapshot
      aiNode(
        "2",
        "AI extract MQL conversion snapshot from webhook",
        280,
        "Extract MQL conversion data from this raw webhook payload. Find the contact email, first name, last name, company name, job title, lead score, lead source, industry, company size, phone number, conversion page URL, and conversion timestamp regardless of field naming. Return JSON: {email, first_name, last_name, company_name, job_title, lead_score, lead_source, industry, company_size, phone, conversion_url, conversion_timestamp}",
        "Raw webhook payload: {webhook_payload}",
        [
          { field: "email", as: "mql_email", type: "string" },
          { field: "first_name", as: "mql_first_name", type: "string" },
          { field: "last_name", as: "mql_last_name", type: "string" },
          { field: "company_name", as: "mql_company", type: "string" },
          { field: "job_title", as: "mql_title", type: "string" },
          { field: "lead_score", as: "mql_score", type: "string" },
          { field: "lead_source", as: "mql_source", type: "string" },
          { field: "industry", as: "mql_industry", type: "string" },
          { field: "company_size", as: "mql_company_size", type: "string" },
          { field: "phone", as: "mql_phone", type: "string" },
          { field: "conversion_url", as: "conversion_url", type: "string" },
          {
            field: "conversion_timestamp",
            as: "conversion_ts",
            type: "string",
          },
        ],
      ),

      // 3. SDR assigner runCode
      n("3", "runCode", "Assign SDR based on territory and round-robin", 520, {
        runCodeDetails: {
          code: `const industry = (variables.mql_industry || '').toLowerCase();
const size = parseInt(variables.mql_company_size) || 0;
const score = parseInt(variables.mql_score) || 0;
const sdrTeam = {
  'enterprise': { name: 'Sarah Chen', email: 'sarah.chen@company.com', slack_id: 'U_SARAH', sla_hours: 2 },
  'mid_market': { name: 'James Wilson', email: 'james.wilson@company.com', slack_id: 'U_JAMES', sla_hours: 4 },
  'smb': { name: 'Maria Garcia', email: 'maria.garcia@company.com', slack_id: 'U_MARIA', sla_hours: 8 },
};
let tier;
if (size >= 500 || score >= 80) tier = 'enterprise';
else if (size >= 100 || score >= 50) tier = 'mid_market';
else tier = 'smb';
const sdr = sdrTeam[tier];
output.assigned_sdr_name = sdr.name;
output.assigned_sdr_email = sdr.email;
output.assigned_sdr_slack = sdr.slack_id;
output.sla_hours = sdr.sla_hours;
output.assignment_tier = tier;
output.sla_deadline = new Date(Date.now() + sdr.sla_hours * 60 * 60 * 1000).toISOString();
output.priority_label = score >= 80 ? 'P1 - Critical' : score >= 50 ? 'P2 - High' : 'P3 - Standard';`,
          outputVariables: [
            { name: "assigned_sdr_name", type: "string" },
            { name: "assigned_sdr_email", type: "string" },
            { name: "assigned_sdr_slack", type: "string" },
            { name: "sla_hours", type: "number" },
            { name: "assignment_tier", type: "string" },
            { name: "sla_deadline", type: "string" },
            { name: "priority_label", type: "string" },
          ],
        },
      }),

      // 4. Follow-up task creator Asana API
      n("4", "hitApi", "Create MQL follow-up task in Asana", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://app.asana.com/api/1.0/tasks",
          headers: [
            { key: "Authorization", value: "Bearer {asana_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "data",
              value:
                '{"name":"[{priority_label}] Follow up MQL: {mql_first_name} {mql_last_name} at {mql_company}","notes":"MQL Details:\\n- Email: {mql_email}\\n- Phone: {mql_phone}\\n- Title: {mql_title}\\n- Company: {mql_company} ({mql_industry}, {mql_company_size} employees)\\n- Score: {mql_score}\\n- Source: {mql_source}\\n- Conversion Page: {conversion_url}\\n- SLA Deadline: {sla_deadline}\\n\\nAction Required: Contact within {sla_hours} hours.","due_at":"{sla_deadline}","projects":["{asana_sdr_project_id}"],"assignee":"{assigned_sdr_email}"}',
            },
          ],
          variables: [
            { name: "data.gid", customName: "asana_task_id", type: "string" },
            {
              name: "data.permalink_url",
              customName: "asana_task_url",
              type: "string",
            },
          ],
        },
      }),

      // 5. SDR alert Slack
      slackNode(
        "5",
        "Alert assigned SDR of new MQL via Slack",
        1000,
        ":zap: *New MQL Assignment — {priority_label}*\\n>*Lead:* {mql_first_name} {mql_last_name} ({mql_email})\\n>*Company:* {mql_company} | {mql_industry} | {mql_company_size} employees\\n>*Title:* {mql_title} | *Score:* {mql_score}\\n>*Source:* {mql_source} | *Page:* {conversion_url}\\n>*Phone:* {mql_phone}\\n>\\n>:clock1: *SLA: Respond within {sla_hours} hours (by {sla_deadline})*\\n>:clipboard: *Task:* {asana_task_url}\\n>\\n>*Assigned to:* <@{assigned_sdr_slack}>",
      ),

      // 6. SLA timer start delay
      n("6", "delayAction", "Start SLA countdown timer", 1240, {
        delayDetails: { duration: 4, unit: "hours" },
      }),

      // 7. SLA breach escalation condition
      n(
        "7",
        "condition",
        "Check if SLA tier requires escalation monitoring",
        1480,
        {
          conditionDetails: {
            conditions: [
              {
                variable: "assignment_tier",
                operator: "equals",
                value: "enterprise",
              },
            ],
            logicalOperator: "AND",
          },
        },
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },
};
